import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import confetti from 'canvas-confetti'
import { MapContainer, TileLayer, useMap, Polygon, Popup, CircleMarker, Circle as LeafletCircle } from 'react-leaflet'
import { 
  MapPin, 
  Info, 
  Layers, 
  Search, 
  Filter, 
  CheckCircle, 
  Edit3,
  Save,
  X,
  BarChart3,
  Target,
  Ban,
  MapPinIcon,
  Circle,
  Grid3X3,
  ArrowRight,
  ChevronDown,
  Shuffle,
  LogOut,
  Moon,
  Sun,
  ChevronLeft,
  HelpCircle,
  MousePointer,
  Lightbulb,
  Navigation,
  Settings,
  Database,
  Monitor,
  Download,
  Upload,
  LocateFixed,
  Loader2,
  AlertCircle,
  AlertTriangle
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { getUserProfile, subscribeToProfile, UserProfile, getAvatarById, getBackgroundColorById } from '../../services/profileService'
import { 
  getGridData, 
  getUserGridProgress, 
  markCellAsExplored, 
  markCellAsUnexplored,
  markCellAsInaccessible,
  updateCellNotes,
  updateCellExploredDate,
  subscribeToUserProgress,
  getExplorationStats,
  updateCustomGridName,
  removeCustomGridName,
  getEnhancedGridData,
  findCellContainingPoint,
  GPS_MARK_MAX_ACCURACY_METERS,
  buildCellBBoxIndex,
  bboxIntersectsViewport,
  viewportsNearlyEqual,
  getDistanceLabelForCell,
  type MapViewport,
  type CellBBox
} from '../../services/gridService'
import { GridCell, UserGridProgress } from '../../types/grid'
import { useNavigate } from 'react-router-dom'

type MapStyleId = 'streets' | 'clean' | 'dark' | 'satellite'

type MapStyleConfig = {
  id: MapStyleId
  name: string
  description: string
  url: string
  attribution: string
  /** Hard map zoom cap — prevents black empty tiles on over-zoom */
  maxZoom: number
  maxNativeZoom: number
  minZoom: number
}

const MAP_STYLES: Record<MapStyleId, MapStyleConfig> = {
  streets: {
    id: 'streets',
    name: 'Streets',
    description: 'Colorful default map',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
    maxNativeZoom: 19,
    minZoom: 10
  },
  clean: {
    id: 'clean',
    name: 'Clean',
    description: 'Soft, low-color map',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
    maxZoom: 20,
    maxNativeZoom: 20,
    minZoom: 10
  },
  dark: {
    id: 'dark',
    name: 'Dark',
    description: 'Dark low-color map',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
    maxZoom: 20,
    maxNativeZoom: 20,
    minZoom: 10
  },
  satellite: {
    id: 'satellite',
    name: 'Satellite',
    description: 'Esri aerial imagery',
    // Esri tile order is z/y/x
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri - Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
    maxZoom: 18,
    maxNativeZoom: 18,
    minZoom: 10
  }
}

const DETAIL_ZOOM = 16

const clampZoomForStyle = (zoom: number, styleId: MapStyleId): number => {
  const style = MAP_STYLES[styleId]
  return Math.min(style.maxZoom, Math.max(style.minZoom, zoom))
}

const MAP_STYLE_STORAGE_KEY = 'gridexplorer-map-style'
const CELEBRATIONS_STORAGE_KEY = 'gridexplorer-celebrations'

const getInitialCelebrationsEnabled = (): boolean => {
  try {
    const saved = localStorage.getItem(CELEBRATIONS_STORAGE_KEY)
    if (saved === 'false') return false
    if (saved === 'true') return true
  } catch {
    // ignore
  }
  return true
}

const buzzExploreHaptic = () => {
  try {
    navigator.vibrate?.(14)
  } catch {
    // iOS Safari and some browsers have no vibrate API
  }
}

/** Below this zoom, hide unexplored grid outlines (explored/inaccessible fills stay). */
const GRID_OUTLINE_MIN_ZOOM = 13

/** Extra map padding so cells don't pop in/out at the edges while panning. */
const VIEWPORT_PAD_RATIO = 0.3

const EXPLORE_CONFETTI_COLORS = ['#10B981', '#34D399', '#6EE7B7', '#FBBF24', '#60A5FA', '#A78BFA']

const fireExploreConfetti = () => {
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  if (prefersReducedMotion) return

  const base = {
    colors: EXPLORE_CONFETTI_COLORS,
    disableForReducedMotion: true as const,
    zIndex: 3000
  }

  confetti({
    ...base,
    particleCount: 90,
    spread: 72,
    startVelocity: 38,
    scalar: 0.95,
    origin: { y: 0.62 }
  })

  window.setTimeout(() => {
    confetti({
      ...base,
      particleCount: 45,
      angle: 60,
      spread: 58,
      startVelocity: 42,
      origin: { x: 0, y: 0.72 }
    })
    confetti({
      ...base,
      particleCount: 45,
      angle: 120,
      spread: 58,
      startVelocity: 42,
      origin: { x: 1, y: 0.72 }
    })
  }, 160)
}

/** One encouragement line per 5% band (no em dashes). */
const PROGRESS_ENCOURAGEMENT: Record<number, string> = {
  0: 'Tap a square or use GPS to begin.',
  5: 'First grids logged. Onward!',
  10: 'Nice start. Keep exploring!',
  15: 'You are finding your rhythm.',
  20: 'One fifth done. Strong pace!',
  25: 'Quarter of the way there!',
  30: 'Momentum is building. Nice!',
  35: 'Over a third complete. Keep it up!',
  40: 'Solid progress. You are rolling!',
  45: 'Almost halfway. Stay curious!',
  50: 'Halfway there. Amazing work!',
  55: 'Past the halfway mark!',
  60: 'Three fifths done. Impressive!',
  65: 'You are deep into the map now.',
  70: 'Seventy percent. So close!',
  75: 'Three quarters complete!',
  80: 'Four fifths done. Incredible!',
  85: 'The finish line is in sight!',
  90: 'Ninety percent. Almost home!',
  95: 'Final stretch. You have got this!',
  100: 'All grids covered. Amazing!'
}

const getProgressEncouragement = (pct: number, completedCells = 0): string => {
  // Begin tip only while they have at most one grid logged
  if (pct <= 0 && completedCells <= 1) return PROGRESS_ENCOURAGEMENT[0]
  if (pct >= 100) return PROGRESS_ENCOURAGEMENT[100]
  // Started (or 1–4%) shares the first "started" line
  if (pct < 5) return PROGRESS_ENCOURAGEMENT[5]
  const band = Math.floor(pct / 5) * 5
  return PROGRESS_ENCOURAGEMENT[band] ?? PROGRESS_ENCOURAGEMENT[5]
}

type ToastType = 'success' | 'warning' | 'error' | 'info'
type AppToast = {
  type: ToastType
  title: string
  message: string
}

/** Detail panel action buttons — shared mobile + desktop */
const DETAIL_BTN_SAVE =
  'flex-1 inline-flex items-center justify-center gap-1.5 min-h-10 px-4 py-2.5 rounded-xl bg-singapore-blue text-white text-sm font-semibold shadow-sm transition-all duration-200 hover:brightness-110 active:scale-[0.98] touch-manipulation'
const DETAIL_BTN_CANCEL =
  'flex-1 inline-flex items-center justify-center gap-1.5 min-h-10 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-primary bg-gray-50 dark:bg-dark-tertiary text-gray-700 dark:text-gray-200 text-sm font-medium transition-all duration-200 hover:bg-gray-100 dark:hover:bg-dark-primary active:scale-[0.98] touch-manipulation'
const DETAIL_BTN_EDIT =
  'inline-flex items-center justify-center gap-1 min-h-9 px-3 py-1.5 rounded-lg text-sm font-medium text-singapore-blue bg-singapore-blue/10 hover:bg-singapore-blue/15 dark:text-blue-300 dark:bg-blue-500/10 dark:hover:bg-blue-500/15 active:scale-[0.98] transition-all duration-200 touch-manipulation flex-shrink-0'

const TOAST_DURATION_MS: Record<ToastType, number> = {
  success: 3500,
  info: 4000,
  warning: 5000,
  error: 6500
}

const getInitialMapStyle = (): MapStyleId => {
  try {
    const saved = localStorage.getItem(MAP_STYLE_STORAGE_KEY)
    if (saved === 'streets' || saved === 'clean' || saved === 'dark' || saved === 'satellite') {
      return saved
    }
  } catch {
    // localStorage may be unavailable
  }
  return 'streets'
}

// Custom hook to handle map center updates
const MapUpdater: React.FC<{ center: [number, number]; zoom?: number; maxZoom: number }> = ({
  center,
  zoom,
  maxZoom
}) => {
  const map = useMap()
  
  useEffect(() => {
    if (map && center) {
      const rawZoom = zoom ?? map.getZoom()
      const targetZoom = Math.min(rawZoom, maxZoom, map.getMaxZoom())
      map.flyTo(center, targetZoom, {
        duration: 1.5,
        easeLinearity: 0.25
      })
    }
  }, [center, zoom, map, maxZoom])
  
  return null
}

/** Keep Leaflet maxZoom in sync with the active tile style (stops black over-zoom). */
const MapZoomLimits: React.FC<{ minZoom: number; maxZoom: number }> = ({ minZoom, maxZoom }) => {
  const map = useMap()

  useEffect(() => {
    map.setMinZoom(minZoom)
    map.setMaxZoom(maxZoom)
    const current = map.getZoom()
    if (current > maxZoom) {
      map.setZoom(maxZoom)
    } else if (current < minZoom) {
      map.setZoom(minZoom)
    }
  }, [map, minZoom, maxZoom])

  return null
}

/** Tracks zoom + padded viewport; rAF-throttled during pan/zoom for smooth culling. */
const MapViewWatcher: React.FC<{
  onViewChange: (view: { zoom: number; viewport: MapViewport }) => void
}> = ({ onViewChange }) => {
  const map = useMap()
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    const publish = () => {
      const bounds = map.getBounds().pad(VIEWPORT_PAD_RATIO)
      onViewChange({
        zoom: map.getZoom(),
        viewport: {
          south: bounds.getSouth(),
          west: bounds.getWest(),
          north: bounds.getNorth(),
          east: bounds.getEast()
        }
      })
    }

    const schedule = () => {
      if (rafRef.current != null) return
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null
        publish()
      })
    }

    publish()
    map.on('move', schedule)
    map.on('zoom', schedule)
    map.on('moveend', publish)
    map.on('zoomend', publish)

    return () => {
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      map.off('move', schedule)
      map.off('zoom', schedule)
      map.off('moveend', publish)
      map.off('zoomend', publish)
    }
  }, [map, onViewChange])

  return null
}

/** True while the map is zooming/panning — used to hide GPS so it does not drift mid-gesture. */
const MapMotionWatcher: React.FC<{
  onMovingChange: (moving: boolean) => void
}> = ({ onMovingChange }) => {
  const map = useMap()

  useEffect(() => {
    const start = () => onMovingChange(true)
    const end = () => onMovingChange(false)

    map.on('zoomstart', start)
    map.on('movestart', start)
    map.on('zoomend', end)
    map.on('moveend', end)

    return () => {
      map.off('zoomstart', start)
      map.off('movestart', start)
      map.off('zoomend', end)
      map.off('moveend', end)
    }
  }, [map, onMovingChange])

  return null
}

// Grid Cell Component
const GridCellComponent: React.FC<{
  cell: GridCell
  isExplored: boolean
  isInaccessible: boolean
  userNotes?: string
  exploredDate?: string
  isDragging: boolean
  showOutlines: boolean
  distanceLabel?: string | null
  onCellClick: (cell: GridCell) => void
  onToggleExplored: (cellId: number, isExplored: boolean) => void
  onMarkInaccessible: (cellId: number) => void
  onUpdateNotes: (cellId: number, notes: string) => void
  onUpdateExploredDate: (cellId: number, date: string) => void
  onUpdateCustomName: (cellId: number, customName: string) => void
  onRemoveCustomName: (cellId: number) => void
  customName?: string
  isCelebrating?: boolean
  onContextMenu: (event: React.MouseEvent, cell: GridCell) => void
  onLongPressStart: (cell: GridCell) => void
  onLongPressEnd: () => void
  isLongPressing?: boolean
  isSelected?: boolean
}> = ({ cell, isExplored, isInaccessible, userNotes, exploredDate, isDragging, showOutlines, distanceLabel, onCellClick, onToggleExplored, onMarkInaccessible, onUpdateNotes, onUpdateExploredDate, onUpdateCustomName, onRemoveCustomName, customName, isCelebrating, onContextMenu, onLongPressStart, onLongPressEnd, isLongPressing, isSelected }) => {
  const [editingNotes, setEditingNotes] = useState(false)
  const [notes, setNotes] = useState(userNotes || '')
  const [editingDate, setEditingDate] = useState(false)
  const [date, setDate] = useState(exploredDate ? new Date(exploredDate).toISOString().split('T')[0] : '')
  const [editingName, setEditingName] = useState(false)
  const [name, setName] = useState(customName || cell.displayName)

  const handleSaveNotes = () => {
    onUpdateNotes(cell.id, notes)
    setEditingNotes(false)
  }

  const handleSaveDate = () => {
    if (date) {
      onUpdateExploredDate(cell.id, new Date(date).toISOString())
    }
    setEditingDate(false)
  }

  const handleSaveName = () => {
    if (name.trim()) {
      if (name.trim() === cell.displayName) {
        // If name is same as default, remove custom name
        onRemoveCustomName(cell.id)
      } else {
        // Set custom name
        onUpdateCustomName(cell.id, name.trim())
      }
    }
    setEditingName(false)
  }

  // Get the display name (custom or default)
  const displayName = customName || cell.displayName

  // Determine grid cell styling based on status
  let fillColor = 'transparent'
  let borderColor = '#6B7280' // gray border for unexplored
  let fillOpacity = 0

  if (isExplored) {
    fillColor = '#10B981' // green
    borderColor = '#059669'
    fillOpacity = 0.6
  } else if (isInaccessible) {
    fillColor = '#EF4444' // red
    borderColor = '#DC2626'
    fillOpacity = 0.6
  }

  // Soft fill when selected — border is drawn by a top overlay so edges stay even
  if (isSelected && !isExplored && !isInaccessible) {
    fillColor = '#F59E0B'
    fillOpacity = 0.08
  }

  // Celebration highlight (pulse handled via higher opacity + thicker border)
  if (isCelebrating) {
    fillColor = '#34D399'
    borderColor = '#FBBF24'
    fillOpacity = 0.85
  }

  // Add fill animation for status changes
  const [isAnimating, setIsAnimating] = useState(false)
  const [animationProgress, setAnimationProgress] = useState(0)
  const [previousStatus, setPreviousStatus] = useState<'unexplored' | 'explored' | 'inaccessible'>('unexplored')
  
  // Determine current status
  const currentStatus = isExplored ? 'explored' : isInaccessible ? 'inaccessible' : 'unexplored'
  
  useEffect(() => {
    // Only trigger animation when status changes from unexplored to explored/inaccessible
    if (previousStatus === 'unexplored' && (currentStatus === 'explored' || currentStatus === 'inaccessible') && !isAnimating) {
      setIsAnimating(true)
      setAnimationProgress(0)
      
      // Start fill animation from bottom to top
      const startTime = Date.now()
      const duration = 1000 // 1 second
      
      const animate = () => {
        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / duration, 1)
        
        setAnimationProgress(progress)
        
        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          setIsAnimating(false)
          setAnimationProgress(1)
        }
      }
      
      requestAnimationFrame(animate)
    }
    
    // Update previous status
    setPreviousStatus(currentStatus)
  }, [currentStatus, previousStatus, isAnimating])

  // Create tooltip content (styling lives in CSS — avoid nested bordered boxes)
  const getTooltipContent = () => {
    const statusLabel = isExplored ? 'Explored' : isInaccessible ? 'Inaccessible' : 'Unexplored'
    const statusClass = isExplored
      ? 'is-explored'
      : isInaccessible
        ? 'is-inaccessible'
        : 'is-unexplored'

    let content = `
      <div class="grid-tooltip">
        <div class="grid-tooltip-title">${displayName}</div>
        ${customName ? `<div class="grid-tooltip-meta">Grid #${cell.id}</div>` : ''}
        <div class="grid-tooltip-status ${statusClass}">${statusLabel}</div>
    `

    if (distanceLabel) {
      content += `<div class="grid-tooltip-distance">${distanceLabel}</div>`
    }

    if ((isExplored || isInaccessible) && exploredDate) {
      const date = new Date(exploredDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
      const dateLabel = isInaccessible ? 'Marked on' : 'Explored'
      content += `<div class="grid-tooltip-meta">${dateLabel} ${date}</div>`
    }

    if (userNotes && userNotes.trim()) {
      const notesSnippet = userNotes.length > 50 ? userNotes.substring(0, 50) + '...' : userNotes
      content += `<div class="grid-tooltip-meta">Notes: ${notesSnippet}</div>`
    }

    if (cell.landmarks && cell.landmarks.length > 0) {
      content += `
        <div class="grid-tooltip-landmarks">
          ${cell.landmarks.map((landmark: any) => `
            <div class="grid-tooltip-meta">• ${landmark.name}</div>
          `).join('')}
        </div>
      `
    }

    content += '</div>'
    return content
  }

  // When zoomed out, skip empty cells so the map stays readable
  if (!showOutlines && !isExplored && !isInaccessible && !isSelected && !isCelebrating) {
    return null
  }

  // Celebration / normal outlines only — selection ring is a separate top overlay
  const outlineWeight = isCelebrating
    ? 4
    : showOutlines
      ? 2
      : (isSelected || isExplored || isInaccessible)
        ? 2
        : 0

  return (
    <>
      {/* Base grid cell with dynamic fill */}
      <Polygon
        positions={cell.bounds as [number, number][]}
        pathOptions={{
          color: borderColor,
          weight: outlineWeight,
          opacity: outlineWeight > 0 ? 1 : 0,
          fillColor: (isExplored || isInaccessible || isSelected) ? fillColor : 'transparent',
          fillOpacity: (isExplored || isInaccessible)
            ? (isAnimating ? (animationProgress * fillOpacity) : fillOpacity)
            : isSelected
              ? fillOpacity
              : 0
        }}
        eventHandlers={{
          click: () => onCellClick(cell),
          contextmenu: (e) => {
            e.originalEvent.preventDefault()
            onContextMenu(e.originalEvent as any, cell)
          },
          mousedown: (e) => {
            if (e.originalEvent.button === 0) { // Left click only
              onLongPressStart(cell)
            }
          },
          mouseup: () => {
            onLongPressEnd()
          },
          mouseout: () => {
            onLongPressEnd()
          },
          // Touch long-press (cast: Leaflet types omit touch* handlers)
          ...({
            touchstart: () => {
              onLongPressStart(cell)
            },
            touchend: () => {
              onLongPressEnd()
            },
            touchcancel: () => {
              onLongPressEnd()
            }
          } as any),
          mouseover: (e) => {
            // Don't open tooltip if map is being dragged or long pressing
            if (isDragging || isLongPressing || !showOutlines) {
              return
            }
            
            const layer = e.target
            
            // Apply the Leaflet fix: override the _openTooltip method
            if (!layer._originalOpenTooltip) {
              layer._originalOpenTooltip = layer._openTooltip;
              layer._openTooltip = function(e: any) {
                if (!this._tooltip || !this._map) {
                  return;
                }
                
                // If the map is moving, we will show the tooltip after it's done.
                if (this._map.dragging && this._map.dragging.moving()) {
                  if (e.type === 'add' && !this._moveEndOpensTooltip) {
                    this._moveEndOpensTooltip = true;
                    var that = this;
                    this._map.once('moveend', function () {
                      that._moveEndOpensTooltip = false;
                      that._openTooltip(e);
                    });
                  }
                  return;
                }
                
                this._tooltip._source = e.layer || e.target;
                this.openTooltip(this._tooltip.options.sticky ? e.latlng : undefined);
              };
            }
            
            layer.bindTooltip(getTooltipContent(), {
              permanent: false,
              direction: 'top',
              className: 'custom-tooltip'
            }).openTooltip()
          }
        }}
      />
    </>
  )
}

const GridExplorer: React.FC = () => {
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const [gridData, setGridData] = useState<any>(null)
  const [userProgress, setUserProgress] = useState<UserGridProgress | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<GridCell[]>([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1)
  const [mapCenter, setMapCenter] = useState<[number, number]>([1.3521, 103.8198])
  const [targetZoom, setTargetZoom] = useState<number>(11)
  const [showStats, setShowStats] = useState(false)
  const [loading, setLoading] = useState(true)
  const [isMapMoving, setIsMapMoving] = useState(false)
  const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'unsaved'>('synced')
  const [showTopMenu, setShowTopMenu] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [userLocation, setUserLocation] = useState<{
    position: [number, number]
    accuracy: number
  } | null>(null)
  const [isLocating, setIsLocating] = useState(false)
  const [toast, setToast] = useState<AppToast | null>(null)
  const [toastOpen, setToastOpen] = useState(false)
  const [mapStyle, setMapStyle] = useState<MapStyleId>(getInitialMapStyle)
  const [showMapStylePicker, setShowMapStylePicker] = useState(false)
  const [mapZoom, setMapZoom] = useState(11)
  const [viewport, setViewport] = useState<MapViewport | null>(null)
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const toastExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cellBBoxIndexRef = useRef<Map<number, CellBBox>>(new Map())
  const handleMapViewChange = useCallback((view: { zoom: number; viewport: MapViewport }) => {
    setMapZoom((prev) => (Math.abs(prev - view.zoom) < 0.01 ? prev : view.zoom))
    setViewport((prev) => (viewportsNearlyEqual(prev, view.viewport) ? prev : view.viewport))
  }, [])
  const mapRef = useRef<any>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchResultsRef = useRef<HTMLDivElement>(null)
  const [celebratingCell, setCelebratingCell] = useState<number | null>(null)
  const [showPlusOne, setShowPlusOne] = useState(false)
  /** Freezes the Done count during celebration so it bumps with +1, not on save */
  const [heldExploredCount, setHeldExploredCount] = useState<number | null>(null)
  const heldExploredCountRef = useRef<number | null>(null)
  const [celebrationsEnabled, setCelebrationsEnabled] = useState(getInitialCelebrationsEnabled)
  const celebrationTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])

  const freezeExploredCount = useCallback((count: number) => {
    heldExploredCountRef.current = count
    setHeldExploredCount(count)
  }, [])

  const clearFrozenExploredCount = useCallback(() => {
    heldExploredCountRef.current = null
    setHeldExploredCount(null)
  }, [])
  /** Map highlight only — separate from the details sheet */
  const [selectedCell, setSelectedCell] = useState<GridCell | null>(null)
  const [showGridDetails, setShowGridDetails] = useState(false)
  const [isModalCollapsed, setIsModalCollapsed] = useState(false)
  const [contextMenu, setContextMenu] = useState<{
    show: boolean
    x: number
    y: number
    cell: GridCell | null
  }>({ show: false, x: 0, y: 0, cell: null })
  const [longPressCell, setLongPressCell] = useState<number | null>(null)
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressTriggeredRef = useRef(false)
  
  // Floating modal editing state
  const [editingName, setEditingName] = useState(false)
  const [editingNameValue, setEditingNameValue] = useState('')
  const [editingDate, setEditingDate] = useState(false)
  const [editingDateValue, setEditingDateValue] = useState('')
  const [editingNotes, setEditingNotes] = useState(false)
  const [editingNotesValue, setEditingNotesValue] = useState('')

  // Global ESC key handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input field
      const target = event.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') {
        return
      }

      if (event.key === 'Escape') {
        // Close any open popups
        const map = mapRef.current?.leafletElement
        if (map) {
          map.closePopup()
        }
        
        // Close search results
        setShowSearchResults(false)
        
        // Close top menu
        setShowTopMenu(false)
        
        // Close modals
        setShowHelp(false)
        setShowSettings(false)
      } else if (event.key === 'S' || event.key === 's') {
        // Focus search box
        event.preventDefault()
        searchInputRef.current?.focus()
      } else if (event.key === 'M' || event.key === 'm') {
        // Toggle top menu
        event.preventDefault()
        setShowTopMenu(!showTopMenu)
      } else if (event.key === 'C' || event.key === 'c') {
        // Zoom out and center on Singapore
        event.preventDefault()
        setMapCenter([1.3521, 103.8198]) // Singapore center coordinates
        setTargetZoom(12) // Zoom out to show all of Singapore
      } else if (event.key === 'H' || event.key === 'h') {
        // Toggle help modal
        event.preventDefault()
        setShowHelp(!showHelp)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showTopMenu, showHelp])

  // Global mouse click handler to close all tooltips
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      // Close search results if clicking outside
      if (searchResultsRef.current && !searchResultsRef.current.contains(event.target as Node)) {
        setShowSearchResults(false)
      }
      
      // Close top menu / map style picker if clicking outside
      const target = event.target as HTMLElement
      if (!target.closest('.top-menu') && !target.closest('.top-menu-button')) {
        setShowTopMenu(false)
      }
      if (!target.closest('.map-style-picker')) {
        setShowMapStylePicker(false)
      }
      
      // Close context menu if clicking outside
      if (!target.closest('.context-menu')) {
        setContextMenu({ show: false, x: 0, y: 0, cell: null })
      }
      
      // Close all tooltips by clicking on the map
      const map = mapRef.current?.leafletElement
      if (map) {
        // Trigger a click on the map to close any open tooltips
        map.fire('click')
      }
    }

    // Add global click listener
    document.addEventListener('click', handleGlobalClick)

    return () => {
      document.removeEventListener('click', handleGlobalClick)
    }
  }, [])

  const dismissToast = useCallback(() => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current)
      toastTimerRef.current = null
    }
    setToastOpen(false)
    if (toastExitTimerRef.current) {
      clearTimeout(toastExitTimerRef.current)
    }
    toastExitTimerRef.current = setTimeout(() => {
      setToast(null)
      toastExitTimerRef.current = null
    }, 220)
  }, [])

  const showToast = useCallback((
    type: ToastType,
    title: string,
    message: string
  ) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current)
      toastTimerRef.current = null
    }
    if (toastExitTimerRef.current) {
      clearTimeout(toastExitTimerRef.current)
      toastExitTimerRef.current = null
    }
    setToast({ type, title, message })
    setToastOpen(false)
    // Double rAF so the closed state paints before opening (re-trigger enter anim)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setToastOpen(true))
    })
    toastTimerRef.current = setTimeout(() => {
      dismissToast()
    }, TOAST_DURATION_MS[type])
  }, [dismissToast])

  const reportSyncFailure = useCallback((message = 'Check your connection and try again.') => {
    setSyncStatus('unsaved')
    showToast('error', 'Could not save', message)
  }, [showToast])

  const toggleCelebrations = useCallback(() => {
    setCelebrationsEnabled((prev) => {
      const next = !prev
      try {
        localStorage.setItem(CELEBRATIONS_STORAGE_KEY, String(next))
      } catch {
        // ignore
      }
      return next
    })
  }, [])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current)
      }
      if (toastExitTimerRef.current) {
        clearTimeout(toastExitTimerRef.current)
      }
      celebrationTimersRef.current.forEach(clearTimeout)
      celebrationTimersRef.current = []
    }
  }, [])

  // Load grid data and user progress
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Loading grid data and user progress...')
        const data = getEnhancedGridData()
        setGridData(data)
        if (data?.cells) {
          cellBBoxIndexRef.current = buildCellBBoxIndex(data.cells)
        }
        console.log('Grid data loaded:', data?.cells?.length, 'cells')
        
        if (user) {
          console.log('Loading user progress for user:', user.uid)
          const progress = await getUserGridProgress(user.uid)
          setUserProgress(progress)
          console.log('User progress loaded:', progress)
        } else {
          console.log('No user logged in')
        }
      } catch (error) {
        console.error('Error loading data:', error)
        showToast('error', 'Connection error', 'Failed to load data. Please check your connection and try again.')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [user, showToast])

  // Subscribe to user progress changes
  useEffect(() => {
    if (!user) return

    console.log('Setting up user progress subscription for user:', user.uid)
    const unsubscribe = subscribeToUserProgress(user.uid, (progress) => {
      console.log('Received user progress update:', progress)
      setUserProgress(progress)
      // Reset sync status when we receive updated data from database
      setSyncStatus('synced')
    })

    return () => {
      console.log('Cleaning up user progress subscription')
      unsubscribe()
    }
  }, [user])

  // Subscribe to user profile changes
  useEffect(() => {
    if (!user) return

    console.log('Setting up user profile subscription for user:', user.uid)
    const unsubscribe = subscribeToProfile(user.uid, (profile) => {
      console.log('Received user profile update:', profile)
      setUserProfile(profile)
    })

    return () => {
      console.log('Cleaning up user profile subscription')
      unsubscribe()
    }
  }, [user])

  const openGridDetails = useCallback((cell: GridCell) => {
    setSelectedCell(cell)
    setShowGridDetails(true)
    setIsModalCollapsed(false)
    setMapCenter(cell.center as [number, number])
    setTargetZoom(clampZoomForStyle(DETAIL_ZOOM, mapStyle))
  }, [mapStyle])

  const handleCellClick = useCallback((cell: GridCell) => {
    // Long-press already opened the context menu — ignore the synthetic click
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false
      return
    }

    setSelectedCell(cell)

    // Mobile: tap only selects/highlights. Open details via long-press menu.
    const isMobile = window.matchMedia('(max-width: 639px)').matches
    if (isMobile) {
      setShowGridDetails(false)
      return
    }

    openGridDetails(cell)
  }, [openGridDetails])

  // Get exploration statistics
  const stats = gridData && userProgress ? getExplorationStats(gridData, userProgress) : null

  // Success animation: focus cell, confetti burst, +1 badge; count bumps with +1
  const triggerSuccessAnimation = useCallback((
    cellId: number,
    cellCenter: [number, number],
    previousExploredCount: number
  ) => {
    celebrationTimersRef.current.forEach(clearTimeout)
    celebrationTimersRef.current = []

    buzzExploreHaptic()

    if (!celebrationsEnabled) {
      clearFrozenExploredCount()
      setCelebratingCell(null)
      setShowPlusOne(false)
      return
    }

    const map = mapRef.current?.leafletElement
    if (map) {
      document.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'Escape',
          code: 'Escape',
          keyCode: 27,
          which: 27,
          bubbles: true
        })
      )
    }

    // Keep freeze (usually already set before save); ensure it stays on the old number
    freezeExploredCount(previousExploredCount)
    setMapCenter(cellCenter)
    setTargetZoom(clampZoomForStyle(DETAIL_ZOOM, mapStyle))
    setCelebratingCell(cellId)
    setShowPlusOne(false)

    const timers: ReturnType<typeof setTimeout>[] = []
    const nextCount = previousExploredCount + 1

    timers.push(
      setTimeout(() => {
        fireExploreConfetti()
        buzzExploreHaptic()

        try {
          const context = new (window.AudioContext || (window as any).webkitAudioContext)()
          const oscillator = context.createOscillator()
          const gainNode = context.createGain()
          oscillator.connect(gainNode)
          gainNode.connect(context.destination)
          oscillator.type = 'sine'
          oscillator.frequency.setValueAtTime(880, context.currentTime)
          oscillator.frequency.exponentialRampToValueAtTime(1320, context.currentTime + 0.12)
          gainNode.gain.setValueAtTime(0.08, context.currentTime)
          gainNode.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.28)
          oscillator.start(context.currentTime)
          oscillator.stop(context.currentTime + 0.3)
        } catch {
          // Audio may be blocked; celebration still works visually
        }

        freezeExploredCount(nextCount)
        setShowPlusOne(true)
      }, 450)
    )

    timers.push(
      setTimeout(() => {
        setShowPlusOne(false)
      }, 2200)
    )

    timers.push(
      setTimeout(() => {
        setCelebratingCell(null)
        clearFrozenExploredCount()
      }, 2600)
    )

    celebrationTimersRef.current = timers
  }, [celebrationsEnabled, freezeExploredCount, clearFrozenExploredCount, mapStyle])

  const handleToggleExplored = useCallback(async (cellId: number, isExplored: boolean) => {
    if (!user) {
      return
    }

    try {
      setSyncStatus('syncing')
      if (isExplored) {
        const previousExploredCount = stats?.exploredCells ?? 0
        // Freeze BEFORE save so Firebase updates cannot flash the new count
        if (celebrationsEnabled) {
          freezeExploredCount(previousExploredCount)
        }
        await markCellAsExplored(user.uid, cellId)
        const cell = gridData?.cells?.find((c: GridCell) => c.id === cellId)
        if (cell) {
          triggerSuccessAnimation(cellId, cell.center as [number, number], previousExploredCount)
        } else {
          buzzExploreHaptic()
          clearFrozenExploredCount()
        }
      } else {
        await markCellAsUnexplored(user.uid, cellId)
      }
      setSyncStatus('synced')
    } catch (error) {
      console.error('Error toggling cell status:', error)
      clearFrozenExploredCount()
      reportSyncFailure()
    }
  }, [
    user,
    gridData,
    stats,
    celebrationsEnabled,
    freezeExploredCount,
    clearFrozenExploredCount,
    triggerSuccessAnimation,
    reportSyncFailure
  ])

  const handleMapStyleChange = useCallback((styleId: MapStyleId) => {
    setMapStyle(styleId)
    setShowMapStylePicker(false)
    try {
      localStorage.setItem(MAP_STYLE_STORAGE_KEY, styleId)
    } catch {
      // ignore storage failures
    }
  }, [])

  const handleLocateMe = useCallback(() => {
    if (!user) {
      showToast('error', 'Login required', 'Please log in to mark grids with GPS.')
      return
    }

    if (!navigator.geolocation) {
      showToast('error', 'GPS unavailable', 'Your browser does not support location.')
      return
    }

    if (!gridData?.cells?.length) {
      showToast('error', 'Grid not ready', 'Grid data is still loading. Try again in a moment.')
      return
    }

    setIsLocating(true)

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude
        const lng = position.coords.longitude
        const accuracy = position.coords.accuracy
        const coords: [number, number] = [lat, lng]

        setUserLocation({ position: coords, accuracy })
        setMapCenter(coords)
        setTargetZoom(clampZoomForStyle(DETAIL_ZOOM, mapStyle))

        const cell = findCellContainingPoint(lat, lng, gridData.cells)

        if (accuracy > GPS_MARK_MAX_ACCURACY_METERS) {
          showToast(
            'warning',
            'Location shown, not marked',
            `GPS accuracy is ±${Math.round(accuracy)}m. Move outdoors and try again (need under ${GPS_MARK_MAX_ACCURACY_METERS}m).`
          )
          setIsLocating(false)
          return
        }

        if (!cell) {
          showToast(
            'warning',
            'Outside the grid',
            'You are not inside a Singapore grid square. Blue dot still shows your location.'
          )
          setIsLocating(false)
          return
        }

        const existing = userProgress?.exploredCells?.[cell.id]
        if (existing?.status === 'explored') {
          showToast(
            'success',
            'You are here',
            `${cell.displayName} is already marked explored.`
          )
          setIsLocating(false)
          return
        }

        if (existing?.status === 'inaccessible') {
          showToast(
            'warning',
            'Marked inaccessible',
            `${cell.displayName} is inaccessible. Change it manually if needed.`
          )
          setIsLocating(false)
          return
        }

        await handleToggleExplored(cell.id, true)
        showToast(
          'success',
          'Marked explored!',
          `${cell.displayName} is now green.`
        )
        setIsLocating(false)
      },
      (geoError) => {
        setIsLocating(false)
        if (geoError.code === geoError.PERMISSION_DENIED) {
          showToast(
            'error',
            'Location blocked',
            'Allow location access in your browser settings, then try again.'
          )
        } else if (geoError.code === geoError.TIMEOUT) {
          showToast('error', 'GPS timed out', 'Could not get a fix in time. Try again outdoors.')
        } else {
          showToast('error', 'GPS failed', 'Could not get your location. Try again.')
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    )
  }, [user, gridData, userProgress, handleToggleExplored, showToast, mapStyle])

  const handleMarkInaccessible = useCallback(async (cellId: number) => {
    if (!user) {
      return
    }

    try {
      setSyncStatus('syncing')
      await markCellAsInaccessible(user.uid, cellId)
      buzzExploreHaptic()
      setSyncStatus('synced')
    } catch (error) {
      console.error('Error marking cell as inaccessible:', error)
      reportSyncFailure()
    }
  }, [user, reportSyncFailure])

  const handleUpdateNotes = useCallback(async (cellId: number, notes: string) => {
    if (!user) return

    try {
      setSyncStatus('syncing')
      await updateCellNotes(user.uid, cellId, notes)
      setSyncStatus('synced')
    } catch (error) {
      console.error('Error updating notes:', error)
      reportSyncFailure()
    }
  }, [user, reportSyncFailure])

  const handleUpdateExploredDate = useCallback(async (cellId: number, date: string) => {
    if (!user) return

    try {
      setSyncStatus('syncing')
      await updateCellExploredDate(user.uid, cellId, date)
      setSyncStatus('synced')
    } catch (error) {
      console.error('Error updating explored date:', error)
      reportSyncFailure()
    }
  }, [user, reportSyncFailure])

  const handleUpdateCustomName = useCallback(async (cellId: number, customName: string) => {
    if (!user) return

    try {
      setSyncStatus('syncing')
      await updateCustomGridName(user.uid, cellId, customName)
      setSyncStatus('synced')
    } catch (error) {
      console.error('Error updating custom name:', error)
      reportSyncFailure()
    }
  }, [user, reportSyncFailure])

  const handleRemoveCustomName = useCallback(async (cellId: number) => {
    if (!user) return

    try {
      setSyncStatus('syncing')
      await removeCustomGridName(user.uid, cellId)
      setSyncStatus('synced')
    } catch (error) {
      console.error('Error removing custom name:', error)
      reportSyncFailure()
    }
  }, [user, reportSyncFailure])

  const handleLogout = async () => {
    try {
      // Set flag for logout transition animation
      sessionStorage.setItem('logoutTransition', 'true')
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Failed to log out:', error)
    }
  }

  // Filter cells based on search and filters (all matching cells; used for counts/search)
  const filteredCells = useMemo(() => {
    if (!gridData?.cells) return [] as GridCell[]

    return gridData.cells.filter((cell: GridCell) => {
      try {
        const trimmedQuery = searchQuery.trim().toLowerCase()
        
        // Handle status filters via search operators
        if (trimmedQuery.startsWith('=')) {
          const statusOperator = trimmedQuery.substring(1)
          const cellStatus = userProgress?.exploredCells?.[cell.id]?.status || 'unexplored'
          
          if (statusOperator === 'explored') {
            return cellStatus === 'explored'
          } else if (statusOperator === 'unexplored') {
            return !userProgress?.exploredCells?.[cell.id]
          } else if (statusOperator === 'inaccessible') {
            return cellStatus === 'inaccessible'
          } else if (statusOperator === 'all') {
            return true // Show all grids
          } else if (statusOperator === 'share') {
            // Share is triggered from the search menu click, not during filter
            return false
          }
          return false // Unknown status operator
        }

        // Range search (e.g., "23-42")
        if (trimmedQuery.includes('-') && !trimmedQuery.includes(' ')) {
          const rangeMatch = trimmedQuery.match(/^(\d+)-(\d+)$/)
          if (rangeMatch) {
            const start = parseInt(rangeMatch[1])
            const end = parseInt(rangeMatch[2])
            if (start <= end && cell.id >= start && cell.id <= end) {
              return true
            }
            return false
          }
        }

        // Regular text search filtering
        const matchesSearch = !trimmedQuery || 
                             (cell.displayName && cell.displayName.toLowerCase().includes(trimmedQuery)) ||
                             (userProgress?.customNames?.[cell.id] && userProgress.customNames[cell.id].toLowerCase().includes(trimmedQuery)) ||
                             (cell.regionName && cell.regionName.toLowerCase().includes(trimmedQuery)) ||
                             cell.id.toString().includes(trimmedQuery) ||
                             (userProgress?.exploredCells?.[cell.id]?.notes && 
                              userProgress.exploredCells[cell.id].notes!.toLowerCase().includes(trimmedQuery)) ||
                             (cell.landmarks && Array.isArray(cell.landmarks) && cell.landmarks.some(landmark => 
                               landmark && landmark.name && landmark.name.toLowerCase().includes(trimmedQuery) ||
                               landmark && landmark.description && landmark.description.toLowerCase().includes(trimmedQuery)
                             )) ||
                             // Always include original grid number in search
                             `grid ${cell.id}`.toLowerCase().includes(trimmedQuery)

        return matchesSearch
      } catch (error) {
        console.error('Error filtering cell:', error)
        return false
      }
    })
  }, [gridData, searchQuery, userProgress])

  const showOutlines = mapZoom >= GRID_OUTLINE_MIN_ZOOM

  // Only mount polygons that intersect the padded viewport (plus pinned cells).
  // When zoomed out, also skip empty cells so we don't mount ~2760 components.
  const visibleCells = useMemo(() => {
    if (!filteredCells.length) return [] as GridCell[]

    const pinnedIds = new Set<number>()
    if (selectedCell) pinnedIds.add(selectedCell.id)
    if (celebratingCell != null) pinnedIds.add(celebratingCell)
    if (userLocation && gridData?.cells) {
      const here = findCellContainingPoint(
        userLocation.position[0],
        userLocation.position[1],
        gridData.cells
      )
      if (here) pinnedIds.add(here.id)
    }

    const bboxIndex = cellBBoxIndexRef.current

    return filteredCells.filter((cell) => {
      if (pinnedIds.has(cell.id)) return true

      if (!showOutlines) {
        const status = userProgress?.exploredCells?.[cell.id]?.status
        if (status !== 'explored' && status !== 'inaccessible') {
          return false
        }
      }

      // Before first map bounds arrive, avoid mounting the full grid
      if (!viewport) return false

      const bbox = bboxIndex.get(cell.id)
      if (!bbox) return true
      return bboxIntersectsViewport(bbox, viewport)
    })
  }, [
    filteredCells,
    viewport,
    selectedCell,
    celebratingCell,
    userLocation,
    gridData,
    showOutlines,
    userProgress
  ])

  // Perform search and update results
  const performSearch = (query: string) => {
    if (!gridData?.cells) return

    // Handle special commands
    if (query.trim() === '=random') {
      // Get all unexplored grids
      const unexploredGrids = gridData.cells.filter((cell: GridCell) => {
        const cellStatus = userProgress?.exploredCells?.[cell.id]?.status
        return !cellStatus || cellStatus !== 'explored' && cellStatus !== 'inaccessible'
      })
      
      if (unexploredGrids.length > 0) {
        // Select a random unexplored grid
        const randomIndex = Math.floor(Math.random() * unexploredGrids.length)
        const randomGrid = unexploredGrids[randomIndex]
        
        // Filter to show only this random grid
        setSearchResults([randomGrid])
        setShowSearchResults(true)
        
        // Navigate to the random grid
        setMapCenter(randomGrid.center as [number, number])
        setTargetZoom(clampZoomForStyle(15, mapStyle))
        
        // Show a brief notification
        console.log(`Random grid selected: Grid ${randomGrid.id}`)
        return
      } else {
        // No unexplored grids available
        setSearchResults([])
        return
      }
    }

    // Regular search logic
    const results = gridData.cells.filter((cell: GridCell) => {
      try {
        const trimmedQuery = query.trim().toLowerCase()
        
        // Handle status filters via search operators
        if (trimmedQuery.startsWith('=')) {
          const statusOperator = trimmedQuery.substring(1)
          const cellStatus = userProgress?.exploredCells?.[cell.id]?.status || 'unexplored'
          
          if (statusOperator === 'explored') {
            return cellStatus === 'explored'
          } else if (statusOperator === 'unexplored') {
            return !userProgress?.exploredCells?.[cell.id]
          } else if (statusOperator === 'inaccessible') {
            return cellStatus === 'inaccessible'
          } else if (statusOperator === 'all') {
            return true
          } else if (statusOperator === 'share') {
            shareUserProgress()
            return false // Don't show any results for share
          }
          return false
        }
        
        // Text search
        const searchableText = [
          cell.id.toString(),
          cell.displayName,
          userProgress?.customNames?.[cell.id] || '',
          userProgress?.exploredCells?.[cell.id]?.notes || '',
          cell.regionName,
          ...(cell.landmarks?.map(l => l.name + ' ' + l.description) || [])
        ].join(' ').toLowerCase()
        
        // Range search (e.g., "23-42")
        if (trimmedQuery.includes('-') && !trimmedQuery.includes(' ')) {
          const rangeMatch = trimmedQuery.match(/^(\d+)-(\d+)$/)
          if (rangeMatch) {
            const start = parseInt(rangeMatch[1])
            const end = parseInt(rangeMatch[2])
            if (start <= end && cell.id >= start && cell.id <= end) {
              return true
            }
            return false
          }
        }
        
        return searchableText.includes(trimmedQuery)
      } catch (error) {
        console.error('Error in search filter:', error)
        return false
      }
    })
    
    setSearchResults(results)
  }

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setSearchQuery(query)
    setSelectedResultIndex(-1) // Reset selection when search changes
    
    if (query.trim()) {
      performSearch(query)
      setShowSearchResults(true)
    } else {
      setSearchResults([])
      setShowSearchResults(false)
    }
  }

  // Handle search input focus - close tooltips
  const handleSearchFocus = () => {
    // Close all tooltips by clicking on the map
    const map = mapRef.current?.leafletElement
    if (map) {
      map.fire('click')
    }
    
    if (searchQuery.trim() && searchResults.length > 0) {
      setShowSearchResults(true)
    }
  }

  // Handle search result selection
  const handleSearchResultSelect = (cell: GridCell) => {
    // Center the map on the selected grid with higher zoom
    setMapCenter(cell.center as [number, number])
    setTargetZoom(clampZoomForStyle(15, mapStyle)) // Zoom in to make grid clearly visible
    setSelectedCell(cell)
    
    // Set the search query to the display name (custom or default)
    const displayName = userProgress?.customNames?.[cell.id] || cell.displayName
    setSearchQuery(displayName)
    setShowSearchResults(false)
    
    // Close any open tooltips after a short delay to ensure map has centered
    setTimeout(() => {
      const map = mapRef.current?.leafletElement
      if (map) {
        map.fire('click')
      }
    }, 100)
    
    // Focus back to search input
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }

  // Handle keyboard navigation for search results
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && showSearchResults && searchResults.length > 0) {
      e.preventDefault()
      if (selectedResultIndex >= 0 && selectedResultIndex < searchResults.length) {
        handleSearchResultSelect(searchResults[selectedResultIndex])
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (showSearchResults && searchResults.length > 0) {
        setSelectedResultIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : 0
        )
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (showSearchResults && searchResults.length > 0) {
        setSelectedResultIndex(prev => 
          prev > 0 ? prev - 1 : searchResults.length - 1
        )
      }
    } else if (e.key === 'Escape') {
      setShowSearchResults(false)
      setSelectedResultIndex(-1)
    }
  }

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts if not typing in an input field
      if (!event.ctrlKey && 
          !event.metaKey && 
          !event.altKey &&
          !(event.target instanceof HTMLInputElement) &&
          !(event.target instanceof HTMLTextAreaElement)) {
        
        // 'S' key - Focus search
        if (event.key.toLowerCase() === 's') {
          event.preventDefault()
          
          // Focus the search input
          if (searchInputRef.current) {
            searchInputRef.current.focus()
            searchInputRef.current.select() // Select existing text for easy replacement
          }
        }
        
        // 'M' key - Toggle menu
        if (event.key.toLowerCase() === 'm') {
          event.preventDefault()
          setShowTopMenu(!showTopMenu)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showTopMenu, showHelp])

  // Context menu handlers
  const handleContextMenu = useCallback((event: React.MouseEvent, cell: GridCell) => {
    event.preventDefault()
    event.stopPropagation()
    try {
      window.getSelection()?.removeAllRanges()
    } catch {
      // ignore
    }

    setSelectedCell(cell)
    setContextMenu({
      show: true,
      x: event.clientX,
      y: event.clientY,
      cell: cell
    })
  }, [])

  const clearTextSelection = useCallback(() => {
    try {
      const selection = window.getSelection()
      selection?.removeAllRanges()
    } catch {
      // ignore
    }
  }, [])

  const handleLongPressStart = useCallback((cell: GridCell) => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
    longPressTriggeredRef.current = false
    setLongPressCell(cell.id)
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true
      clearTextSelection()
      setSelectedCell(cell)
      // Show context menu at center of screen for long press
      setContextMenu({
        show: true,
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
        cell: cell
      })
      setLongPressCell(null)
      longPressTimerRef.current = null
    }, 500)
  }, [clearTextSelection])

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
    setLongPressCell(null)
  }, [])

  const handleShowGridDetailsFromMenu = useCallback(() => {
    const cell = contextMenu.cell
    setContextMenu({ show: false, x: 0, y: 0, cell: null })
    if (cell) {
      openGridDetails(cell)
    }
  }, [contextMenu.cell, openGridDetails])

  const handleContextMenuAction = useCallback(async (action: 'explored' | 'unexplored' | 'inaccessible') => {
    if (!contextMenu.show || !contextMenu.cell || !user) return

    const { cell } = contextMenu
    
    try {
      setSyncStatus('syncing')
      
      if (action === 'explored') {
        const previousExploredCount = stats?.exploredCells ?? 0
        if (celebrationsEnabled) {
          freezeExploredCount(previousExploredCount)
        }
        await markCellAsExplored(user.uid, cell.id)
        const gridCell = gridData?.cells?.find((c: GridCell) => c.id === cell.id)
        if (gridCell) {
          triggerSuccessAnimation(cell.id, gridCell.center as [number, number], previousExploredCount)
        } else {
          buzzExploreHaptic()
          clearFrozenExploredCount()
        }
      } else if (action === 'inaccessible') {
        await markCellAsInaccessible(user.uid, cell.id)
        buzzExploreHaptic()
      } else if (action === 'unexplored') {
        await markCellAsUnexplored(user.uid, cell.id)
      }
      
      setSyncStatus('synced')
    } catch (error) {
      console.error('Error updating cell status:', error)
      clearFrozenExploredCount()
      reportSyncFailure()
    }
    
    setContextMenu({ show: false, x: 0, y: 0, cell: null })
  }, [
    contextMenu,
    user,
    gridData,
    stats,
    celebrationsEnabled,
    freezeExploredCount,
    clearFrozenExploredCount,
    triggerSuccessAnimation,
    reportSyncFailure
  ])

  const handleStatsClick = (filterType: 'explored' | 'unexplored' | 'inaccessible') => {
    // Set the search query to filter the map
    const searchOperator = `=${filterType}`
    setSearchQuery(searchOperator)
    
    // Zoom out and center on Singapore to show the filtered results
    setMapCenter([1.3521, 103.8198])
    setTargetZoom(12)
    
    // Close any open modals or menus
    setShowGridDetails(false)
    setSelectedCell(null)
    setShowTopMenu(false)
    setShowSearchResults(false)
  }

  // Helper function to close details sheet (keep map selection highlight)
  const closeModal = () => {
    setShowGridDetails(false)
    setIsModalCollapsed(false)
  }

  // Helper function to format dates
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const day = date.getDate().toString().padStart(2, '0')
    const month = date.toLocaleString('en-US', { month: 'short' })
    const year = date.getFullYear()
    const weekday = date.toLocaleString('en-US', { weekday: 'short' })
    return `${day} ${month} ${year} (${weekday})`
  }

  // Data Export/Import Functions
  const exportUserData = () => {
    if (!user || !userProgress) return

    const exportData = {
      version: '1.0',
      exportDate: new Date().toISOString(),
      user: {
        uid: user.uid,
        email: user.email,
        profile: userProfile
      },
      explorationData: {
        exploredCells: userProgress.exploredCells || {},
        customNames: userProgress.customNames || {},
        lastUpdated: userProgress.lastUpdated
      }
    }

    const dataStr = JSON.stringify(exportData, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = `singapore-grid-explorer-${user.uid}-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const importUserData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !user) return

    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const content = e.target?.result as string
        const importData = JSON.parse(content)

        // Validate the import data
        if (!importData.version || !importData.explorationData) {
          showToast('error', 'Invalid file', 'Please use a file exported from Grid Explorer.')
          return
        }

        // Show confirmation dialog
        const confirmed = window.confirm(
          'This will merge the imported data with your current data. Grids that already have data will be updated with the imported data. Continue?'
        )

        if (confirmed) {
          // Merge the data
          const currentData = userProgress || {} as UserGridProgress
          const importedData = importData.explorationData

          // Merge explored cells
          const mergedExploredCells = {
            ...(currentData.exploredCells || {}),
            ...(importedData.exploredCells || {})
          }

          // Merge custom names
          const mergedCustomNames = {
            ...(currentData.customNames || {}),
            ...(importedData.customNames || {})
          }

          // Update the user's data in Firebase
          const updatedProgress = {
            ...currentData,
            exploredCells: mergedExploredCells,
            customNames: mergedCustomNames,
            lastUpdated: new Date().toISOString()
          }

          // Save to Firebase - we'll need to implement a proper bulk update function
          // For now, just show a success message
          void updatedProgress
          showToast(
            'warning',
            'Import not fully saved',
            'File was read, but full import to the cloud still needs more work. Your live data was not overwritten.'
          )
        }
      } catch (error) {
        console.error('Error importing data:', error)
        showToast('error', 'Import failed', 'Could not read that file. Check the format and try again.')
      }
    }
    reader.readAsText(file)
  }

  const shareUserProgress = async () => {
    if (!user || !userProgress || !stats || !gridData) return

    const totalGrids = gridData.cells.length
    const completionPercentage = Math.round((stats.exploredCells / totalGrids) * 100)
    
    // Create timestamp in the format "28 Jun 2025 (Sat) 8:00pm"
    const now = new Date()
    const day = now.getDate().toString().padStart(2, '0')
    const month = now.toLocaleString('en-US', { month: 'short' })
    const year = now.getFullYear()
    const weekday = now.toLocaleString('en-US', { weekday: 'short' })
    const time = now.toLocaleString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })
    const timestamp = `${day} ${month} ${year} (${weekday}) ${time}`
    
    const shareMessage = `🗺️ Singapore Grid Explorer Progress

✅ Explored: ${stats.exploredCells} grids
❌ Inaccessible: ${stats.inaccessibleCells} grids  
⭕ Unexplored: ${stats.unexploredCells} grids

📊 Total Progress: ${stats.exploredCells}/${totalGrids} (${completionPercentage}% complete)

🌐 Explore Singapore's grid system: 
https://gridexplorer.vercel.app

📅 ${timestamp}`

    try {
      await navigator.clipboard.writeText(shareMessage)
      setSearchQuery('')
      setShowSearchResults(false)
      showToast('success', 'Copied!', 'Your progress message is on the clipboard.')
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
      showToast('error', 'Copy failed', 'Could not copy to clipboard. Try again or copy manually.')
    }
  }

  // Lock Safari viewport while explorer is open (100dvh + no overscroll letterboxing)
  useEffect(() => {
    document.documentElement.classList.add('explorer-active')
    document.body.classList.add('explorer-active')

    const preventSelectStart = (event: Event) => {
      const target = event.target as HTMLElement | null
      if (target?.closest?.('input, textarea, [contenteditable="true"]')) return
      event.preventDefault()
    }

    document.addEventListener('selectstart', preventSelectStart)

    return () => {
      document.documentElement.classList.remove('explorer-active')
      document.body.classList.remove('explorer-active')
      document.removeEventListener('selectstart', preventSelectStart)
    }
  }, [])

  if (loading) {
    return (
      <div className="explorer-shell items-center justify-center bg-gray-50 dark:bg-dark-primary">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-singapore-red"></div>
      </div>
    )
  }

  // Error boundary for the main component
  if (!gridData || !gridData.cells) {
    return (
      <div className="explorer-shell items-center justify-center bg-gray-50 dark:bg-dark-primary">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">Error Loading Grid Data</div>
          <p className="text-gray-600 dark:text-dark-secondary">Please refresh the page to try again.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="explorer-shell bg-gray-50 dark:bg-dark-primary">
      {/* Top-left controls: Menu, GPS, Map style */}
      <div className="fixed top-4 left-4 z-[1000] flex flex-col items-start gap-2 top-menu-button">
        {/* Space reserved when hidden so GPS / layers do not jump up */}
        <button
          onClick={() => setShowTopMenu(!showTopMenu)}
          className={`top-menu-trigger p-3 sm:p-3 bg-white dark:bg-dark-secondary rounded-xl shadow-lg border border-gray-200 dark:border-dark-primary transition-all duration-300 hover:bg-gray-50 dark:hover:bg-dark-tertiary hover:scale-105 hover:shadow-xl active:scale-95 ${
            showTopMenu ? 'is-hidden' : ''
          }`}
          aria-hidden={showTopMenu}
          tabIndex={showTopMenu ? -1 : 0}
          aria-expanded={showTopMenu}
          aria-label="Open menu"
        >
          <div className="flex items-center space-x-2">
            <div className="p-1 bg-singapore-red/10 rounded transition-all duration-300 hover:bg-singapore-red/20">
              <MapPin className="h-4 w-4 sm:h-4 sm:w-4 text-singapore-red transition-transform duration-300 hover:rotate-12" />
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:block">Menu</span>
          </div>
        </button>

        <button
          onClick={handleLocateMe}
          disabled={isLocating}
          className="p-3 bg-white dark:bg-dark-secondary rounded-xl shadow-lg border border-gray-200 dark:border-dark-primary transition-all duration-300 hover:bg-blue-50 dark:hover:bg-dark-tertiary hover:scale-105 hover:shadow-xl active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
          title="Use my GPS location"
          aria-label="Use my GPS location"
        >
          {isLocating ? (
            <Loader2 className="h-5 w-5 text-singapore-blue animate-spin" />
          ) : (
            <LocateFixed className="h-5 w-5 text-singapore-blue" />
          )}
        </button>

        <div className="relative map-style-picker">
          <button
            onClick={() => setShowMapStylePicker(!showMapStylePicker)}
            className="p-3 bg-white dark:bg-dark-secondary rounded-xl shadow-lg border border-gray-200 dark:border-dark-primary transition-all duration-300 hover:bg-gray-50 dark:hover:bg-dark-tertiary hover:scale-105 hover:shadow-xl active:scale-95"
            title="Map style"
            aria-label="Choose map style"
            aria-expanded={showMapStylePicker}
          >
            <Layers className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          </button>

          <div
            className={`ui-popover ui-popover-from-left absolute top-0 left-full ml-2 w-52 bg-white dark:bg-dark-secondary rounded-xl shadow-xl border border-gray-200 dark:border-dark-primary overflow-hidden ${
              showMapStylePicker ? 'is-open' : ''
            }`}
            aria-hidden={!showMapStylePicker}
          >
            {(Object.values(MAP_STYLES) as Array<(typeof MAP_STYLES)[MapStyleId]>).map((style) => (
              <button
                key={style.id}
                onClick={() => handleMapStyleChange(style.id)}
                tabIndex={showMapStylePicker ? 0 : -1}
                className={`w-full text-left px-3 py-2.5 transition-colors ${
                  mapStyle === style.id
                    ? 'bg-singapore-blue/10 text-singapore-blue'
                    : 'hover:bg-gray-50 dark:hover:bg-dark-tertiary text-gray-800 dark:text-gray-200'
                }`}
              >
                <div className="text-sm font-medium">{style.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{style.description}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Dropdown Menu Bar - Top (always mounted so slide-up/down can animate) */}
      <div
        className={`top-menu top-menu-bar fixed top-0 left-0 right-0 z-[2000] bg-white dark:bg-dark-secondary border-b border-gray-200 dark:border-dark-primary shadow-lg ${
          showTopMenu ? 'is-open' : ''
        }`}
        aria-hidden={!showTopMenu}
      >
          <div className="flex items-center justify-between p-3 sm:p-4">
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="p-2 bg-singapore-red/10 rounded-lg transition-all duration-300 hover:bg-singapore-red/20">
                  <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-singapore-red transition-transform duration-300 hover:rotate-12" />
                </div>
                <div>
                  <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-dark-primary">Grid Explorer</h1>
                  <p className="text-xs text-gray-600 dark:text-dark-secondary hidden sm:block">
                    Explore the grid and track your progress
                  </p>
                </div>
              </div>
              
              {/* Navigation Links - Hidden on mobile */}
              <div className="hidden sm:flex items-center space-x-4">
                <button 
                  onClick={() => setShowSettings(true)}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-all duration-300 hover:scale-105"
                  tabIndex={showTopMenu ? 0 : -1}
                >
                  Settings
                </button>
                <button 
                  onClick={() => setShowHelp(!showHelp)}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-all duration-300 hover:scale-105"
                  tabIndex={showTopMenu ? 0 : -1}
                >
                  Help
                </button>
              </div>
            </div>
            
            {/* User Info & Actions */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-gray-100 dark:bg-dark-tertiary hover:bg-gray-200 dark:hover:bg-dark-primary transition-all duration-300 hover:scale-110 hover:rotate-12"
                title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
                tabIndex={showTopMenu ? 0 : -1}
              >
                {theme === 'light' ? (
                  <Moon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700 dark:text-dark-secondary" />
                ) : (
                  <Sun className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700 dark:text-dark-secondary" />
                )}
              </button>

              {user && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigate('/profile')}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg cursor-pointer overflow-hidden ${
                      userProfile?.backgroundColor ? 
                        getBackgroundColorById(userProfile.backgroundColor).value : 
                        'bg-gradient-to-br from-singapore-blue to-singapore-red'
                    }`}
                    tabIndex={showTopMenu ? 0 : -1}
                  >
                    <span className="text-white text-xs sm:text-sm font-medium">
                      {userProfile?.avatarId ? 
                        getAvatarById(userProfile.avatarId).emoji : 
                        (userProfile?.nickname?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U')
                      }
                    </span>
                  </button>
                  <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 hidden sm:block">
                    {userProfile?.nickname || user.email}
                  </span>
                </div>
              )}
              <button 
                onClick={handleLogout}
                className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-all duration-300 hover:scale-105"
                tabIndex={showTopMenu ? 0 : -1}
              >
                <LogOut className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:block">Logout</span>
              </button>
              
              {/* Close Button */}
              <button
                onClick={() => setShowTopMenu(false)}
                className="p-2 rounded-lg bg-gray-100 dark:bg-dark-tertiary hover:bg-gray-200 dark:hover:bg-dark-primary transition-all duration-300 hover:scale-110 hover:rotate-90"
                title="Close Menu"
                tabIndex={showTopMenu ? 0 : -1}
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700 dark:text-dark-secondary" />
              </button>
            </div>
          </div>
      </div>

      {/* Map Container - Fill remaining space */}
      <div className="explorer-map">
        {/* Search Box - Top Right */}
        <div className="fixed top-4 right-4 z-[1000] w-64 sm:w-80">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onKeyDown={handleSearchKeyDown}
              className="w-full pl-10 pr-10 border border-gray-200 dark:border-dark-primary rounded-xl focus:outline-none focus:ring-2 focus:ring-singapore-blue focus:border-transparent bg-white dark:bg-dark-tertiary text-gray-900 dark:text-dark-primary placeholder-gray-400 dark:placeholder-dark-tertiary transition-all duration-300 shadow-lg hover:shadow-xl focus:shadow-2xl focus:scale-[1.02] text-base sm:text-base"
            />
            
            {/* Clear Button */}
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('')
                  setShowSearchResults(false)
                  searchInputRef.current?.focus()
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-dark-primary transition-colors duration-200"
              >
                <X className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
              </button>
            )}
            
            {/* Search Results Dropdown */}
              <div
                ref={searchResultsRef}
                className={`ui-popover ui-popover-from-top absolute top-full left-0 right-0 mt-2 bg-white dark:bg-dark-secondary border border-gray-200 dark:border-dark-primary rounded-xl shadow-xl z-[9999] max-h-64 sm:max-h-80 overflow-y-auto ${
                  showSearchResults ? 'is-open' : ''
                }`}
                aria-hidden={!showSearchResults}
              >
                {searchQuery.trim().startsWith('=') && (
                  <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 dark:bg-dark-tertiary border-b border-gray-200 dark:border-dark-primary rounded-t-xl">
                    <p className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">Status Filters</p>
                    <div className="space-y-1 sm:space-y-2">
                      {['explored', 'unexplored', 'inaccessible', 'random', 'all', 'share'].filter(option => 
                        option.startsWith(searchQuery.trim().substring(1).toLowerCase())
                      ).map(option => (
                        <button
                          key={option}
                          onClick={() => {
                            if (option === 'share') {
                              shareUserProgress()
                            } else {
                              setSearchQuery(`=${option}`)
                              performSearch(`=${option}`)
                            }
                          }}
                          className="w-full flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 text-left hover:bg-white dark:hover:bg-dark-secondary rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-dark-primary transition-all duration-200 group"
                        >
                          <div className={`flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${
                            option === 'explored' ? 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400' :
                            option === 'unexplored' ? 'bg-gray-100 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400' :
                            option === 'inaccessible' ? 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400' :
                            option === 'random' ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400' :
                            option === 'share' ? 'bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400' :
                            'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                          }`}>
                            {option === 'explored' ? (
                              <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                            ) : option === 'unexplored' ? (
                              <Circle className="h-3 w-3 sm:h-4 sm:w-4" />
                            ) : option === 'inaccessible' ? (
                              <Ban className="h-3 w-3 sm:h-4 sm:w-4" />
                            ) : option === 'random' ? (
                              <Shuffle className="h-3 w-3 sm:h-4 sm:w-4" />
                            ) : option === 'share' ? (
                              <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                            ) : (
                              <Grid3X3 className="h-3 w-3 sm:h-4 sm:w-4" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 dark:text-dark-primary text-xs sm:text-sm">
                              ={option}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                              {option === 'random' ? 'Go to a random unexplored grid' : 
                               option === 'share' ? 'Share your exploration progress' : 
                               `Show all ${option} grids`}
                            </div>
                          </div>
                          <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {searchResults.length > 0 ? (
                  <>
                    <div className="px-4 py-3 bg-gray-50 dark:bg-dark-tertiary border-b border-gray-200 dark:border-dark-primary rounded-t-xl">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
                      </p>
                    </div>
                    {searchResults.map((cell, index) => {
                      try {
                        const isExplored = userProgress?.exploredCells?.[cell.id]?.status === 'explored'
                        const isInaccessible = userProgress?.exploredCells?.[cell.id]?.status === 'inaccessible'
                        const exploredDate = userProgress?.exploredCells?.[cell.id]?.exploredDate
                        const customName = userProgress?.customNames?.[cell.id]
                        const isSelected = index === selectedResultIndex
                        
                        return (
                          <button
                            key={cell.id}
                            onClick={() => handleSearchResultSelect(cell)}
                            className={`w-full flex items-center space-x-3 p-3 text-left border-b border-gray-100 dark:border-dark-primary last:border-b-0 transition-colors duration-200 group ${
                              isSelected 
                                ? 'bg-singapore-blue/10 dark:bg-singapore-blue/20 border-singapore-blue/20' 
                                : 'hover:bg-gray-50 dark:hover:bg-dark-tertiary'
                            }`}
                          >
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                              isExplored ? 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400' :
                              isInaccessible ? 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400' :
                              'bg-gray-100 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400'
                            }`}>
                              {isExplored ? (
                                <CheckCircle className="h-4 w-4" />
                              ) : isInaccessible ? (
                                <Ban className="h-4 w-4" />
                              ) : (
                                <Circle className="h-4 w-4" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 dark:text-dark-primary truncate">
                                {customName || `Grid ${cell.id}`}
                                {customName && (
                                  <span className="ml-2 text-xs bg-gray-100 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400 px-2 py-1 rounded">
                                    #{cell.id}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                {exploredDate && isInaccessible ? (
                                  `Marked on ${formatDate(exploredDate)}`
                                ) : exploredDate && isExplored ? (
                                  `Explored ${formatDate(exploredDate)}`
                                ) : (
                                  `Grid ${cell.id} • ${cell.regionName}`
                                )}
                              </div>
                            </div>
                            <div className={`flex-shrink-0 transition-opacity duration-200 ${
                              isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                            }`}>
                              <ArrowRight className="h-4 w-4 text-gray-400" />
                            </div>
                          </button>
                        )
                      } catch (error) {
                        console.error('Error rendering search result:', error)
                        return null
                      }
                    })}
                  </>
                ) : (
                  <div className="px-4 py-8 text-center">
                    <div className="text-gray-400 dark:text-gray-500 text-sm">No results found</div>
                  </div>
                )}
              </div>
          </div>
        </div>

        {/* Context Menu */}
          <div 
            className={`ui-context-menu fixed z-[2000] bg-white dark:bg-dark-secondary rounded-xl shadow-xl border border-gray-200 dark:border-dark-primary p-3 sm:p-4 min-w-64 sm:min-w-72 ${
              contextMenu.show ? 'is-open' : ''
            }`}
            style={{
              left: contextMenu.x,
              top: contextMenu.y
            }}
            aria-hidden={!contextMenu.show}
          >
            <div className="mb-3 sm:mb-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="p-2 bg-singapore-red/10 rounded-lg">
                  <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-singapore-red" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-dark-primary text-sm sm:text-base">
                    {contextMenu.cell?.displayName}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {contextMenu.cell?.regionName}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="py-1">
              <button
                type="button"
                onClick={handleShowGridDetailsFromMenu}
                className="w-full flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-2 sm:py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors duration-200 group"
              >
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                  <Info className="h-3 w-3 sm:h-4 sm:w-4 text-singapore-blue dark:text-blue-400" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-dark-primary text-xs sm:text-sm">Show grid details</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">Name, date, and notes</div>
                </div>
              </button>

              <button
                onClick={() => handleContextMenuAction('explored')}
                className="w-full flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-2 sm:py-3 text-left hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors duration-200 group"
              >
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-dark-primary text-xs sm:text-sm">Mark as Explored</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">Completed this area</div>
                </div>
              </button>
              
              <button
                onClick={() => handleContextMenuAction('inaccessible')}
                className="w-full flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-2 sm:py-3 text-left hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200 group"
              >
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                  <Ban className="h-3 w-3 sm:h-4 sm:w-4 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-dark-primary text-xs sm:text-sm">Mark as Inaccessible</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">Cannot be explored</div>
                </div>
              </button>
              
              <button
                onClick={() => handleContextMenuAction('unexplored')}
                className="w-full flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-2 sm:py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-900/20 rounded-lg transition-colors duration-200 group"
              >
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-100 dark:bg-gray-900/20 rounded-full flex items-center justify-center">
                  <Circle className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-dark-primary text-xs sm:text-sm">Mark as Unexplored</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">Reset to unvisited</div>
                </div>
              </button>
            </div>
          </div>

        {/* +1 celebration badge near stats */}
        {showPlusOne && (
          <div
            className="fixed left-4 z-[1001] pointer-events-none"
            style={{ bottom: 'max(5.5rem, calc(env(safe-area-inset-bottom, 0px) + 5rem))' }}
          >
            <div className="animate-explore-plus-one inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1.5 text-sm font-bold text-white shadow-lg shadow-emerald-500/40 ring-2 ring-white/80 dark:ring-neutral-900/80">
              <CheckCircle className="h-4 w-4" aria-hidden="true" />
              +1 explored
            </div>
          </div>
        )}

        {/* Compact Floating Stats Panel - Bottom (safe-area aware for phones) */}
        {stats && (
          <div
            className="fixed left-4 z-[1000] max-w-[calc(100vw-2rem)]"
            style={{ bottom: 'max(1.5rem, calc(env(safe-area-inset-bottom, 0px) + 1rem))' }}
          >
            <div
              className={`bg-white dark:bg-dark-secondary rounded-xl shadow-lg border border-gray-200 dark:border-dark-primary p-2 sm:p-3 backdrop-blur-sm bg-opacity-95 dark:bg-opacity-95 transition-all duration-300 overflow-hidden max-sm:w-[calc(100vw-2rem)] max-sm:active:scale-[0.99] ${
                showStats ? '' : 'sm:hover:shadow-xl sm:hover:scale-[1.02]'
              } max-sm:cursor-pointer`}
              onClick={() => {
                // Phones: tap anywhere on the bar to expand/collapse
                if (window.matchMedia('(max-width: 639px)').matches) {
                  setShowStats((prev) => !prev)
                }
              }}
              title={showStats ? 'Tap to hide progress' : 'Tap to show progress'}
            >
              <div className="flex items-center gap-2 sm:gap-4 min-w-0 w-full">
                {/* Progress Stats — equal 3-column grid on phones; numbers only filter on desktop/tablet */}
                <div className="grid grid-cols-3 gap-x-4 w-full min-w-0 sm:flex sm:w-auto sm:gap-3 sm:items-center">
                  <div
                    className="min-w-0 text-center sm:cursor-pointer sm:transition-transform sm:duration-300 sm:hover:scale-110"
                    onClick={(event) => {
                      if (window.matchMedia('(min-width: 640px)').matches) {
                        event.stopPropagation()
                        handleStatsClick('explored')
                      }
                    }}
                  >
                    <div className={`text-sm sm:text-lg font-bold text-green-600 transition-all duration-500 ${showPlusOne ? 'animate-number-bounce' : ''}`}>
                      {heldExploredCountRef.current ?? heldExploredCount ?? stats.exploredCells}
                    </div>
                    <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                      <span className="sm:hidden">Done</span>
                      <span className="hidden sm:inline">Explored</span>
                    </div>
                  </div>
                  <div
                    className="min-w-0 text-center sm:cursor-pointer sm:transition-transform sm:duration-300 sm:hover:scale-110"
                    onClick={(event) => {
                      if (window.matchMedia('(min-width: 640px)').matches) {
                        event.stopPropagation()
                        handleStatsClick('inaccessible')
                      }
                    }}
                  >
                    <div className="text-sm sm:text-lg font-bold text-red-600">{stats.inaccessibleCells}</div>
                    <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                      <span className="sm:hidden">Inaccessible</span>
                      <span className="hidden sm:inline">Inaccessible</span>
                    </div>
                  </div>
                  <div
                    className="min-w-0 text-center sm:cursor-pointer sm:transition-transform sm:duration-300 sm:hover:scale-110"
                    onClick={(event) => {
                      if (window.matchMedia('(min-width: 640px)').matches) {
                        event.stopPropagation()
                        handleStatsClick('unexplored')
                      }
                    }}
                  >
                    <div className="text-sm sm:text-lg font-bold text-orange-500">{stats.unexploredCells}</div>
                    <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                      <span className="sm:hidden">Left</span>
                      <span className="hidden sm:inline">Unexplored</span>
                    </div>
                  </div>
                </div>
                
                {/* Compact % — desktop/tablet; phones use the expanded completion panel */}
                <div className="hidden sm:flex items-center space-x-2" title="Overall completion">
                  <div className="w-16 bg-gray-200 dark:bg-dark-primary rounded-full h-2 overflow-hidden flex">
                    <div
                      className="bg-emerald-500 h-2 transition-all duration-700 ease-out"
                      style={{ width: `${(stats.exploredCells / Math.max(stats.totalCells, 1)) * 100}%` }}
                    />
                    <div
                      className="bg-red-500 h-2 transition-all duration-700 ease-out"
                      style={{ width: `${(stats.inaccessibleCells / Math.max(stats.totalCells, 1)) * 100}%` }}
                    />
                  </div>
                  <div className="text-sm font-semibold tabular-nums text-emerald-600 dark:text-emerald-400 min-w-[2.5rem]">
                    {Math.round(stats.progressPercentage)}%
                  </div>
                </div>

                {/* Sync Status — desktop/tablet only */}
                <div className="hidden sm:flex items-center space-x-1 sm:space-x-2">
                  {syncStatus === 'synced' && (
                    <>
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-green-600 dark:text-green-400">Saved</span>
                    </>
                  )}
                  {syncStatus === 'syncing' && (
                    <>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Saving...</span>
                    </>
                  )}
                  {syncStatus === 'unsaved' && (
                    <>
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-red-600 dark:text-red-400">Error</span>
                    </>
                  )}
                </div>

                {/* Expand Button — desktop/tablet only; phones tap the whole bar */}
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    setShowStats(!showStats)
                  }}
                  className={`hidden sm:inline-flex p-1.5 rounded-lg transition-colors duration-200 ${
                    showStats
                      ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600'
                      : 'hover:bg-gray-100 dark:hover:bg-dark-tertiary text-gray-500 dark:text-gray-400'
                  }`}
                  title={showStats ? 'Hide progress' : 'Show progress'}
                  aria-expanded={showStats}
                >
                  <BarChart3 className="h-4 w-4" />
                </button>
              </div>

              {/* Expanded completion panel — height + fade animate open/close */}
              {(() => {
                const total = Math.max(stats.totalCells, 1)
                const exploredPct = (stats.exploredCells / total) * 100
                const inaccessiblePct = (stats.inaccessibleCells / total) * 100
                const pct = Math.round(stats.progressPercentage)
                const encouragement = getProgressEncouragement(pct, stats.completedCells)
                const regionEntries = Object.entries(stats.regionStats)
                const showRegions = regionEntries.length > 1

                return (
                  <div
                    className={`stats-expand ${showStats ? 'is-open' : ''}`}
                    aria-hidden={!showStats}
                  >
                    <div className="stats-expand-inner">
                      <div className={`stats-expand-content mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200 dark:border-dark-primary space-y-2.5 ${showStats ? '' : 'pointer-events-none'}`}>
                        <div className="rounded-xl bg-gray-50 dark:bg-dark-tertiary px-3 py-2.5">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="min-w-0">
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                Overall progress
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 leading-snug">
                                {encouragement}
                              </p>
                            </div>
                            <div className="flex-shrink-0 text-right">
                              <div className="text-2xl font-bold tabular-nums leading-none text-emerald-600 dark:text-emerald-400">
                                {pct}%
                              </div>
                              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 tabular-nums">
                                {stats.completedCells}/{stats.totalCells}
                              </div>
                            </div>
                          </div>

                          <div
                            className="h-3 rounded-full bg-gray-200 dark:bg-dark-primary overflow-hidden flex shadow-inner"
                            role="progressbar"
                            aria-valuenow={pct}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`Overall progress ${pct} percent`}
                          >
                            <div
                              className="h-full bg-emerald-500 transition-all duration-700 ease-out"
                              style={{ width: `${exploredPct}%` }}
                              title={`${stats.exploredCells} explored`}
                            />
                            <div
                              className="h-full bg-red-500 transition-all duration-700 ease-out"
                              style={{ width: `${inaccessiblePct}%` }}
                              title={`${stats.inaccessibleCells} inaccessible`}
                            />
                          </div>
                        </div>

                        {showRegions && (
                          <div className="space-y-1.5 max-h-28 sm:max-h-36 overflow-y-auto pr-0.5">
                            {regionEntries.map(([region, data]: [string, any]) => {
                              const regionPct = Math.round((data.explored / Math.max(data.total, 1)) * 100)
                              return (
                                <div key={region} className="rounded-lg bg-gray-50 dark:bg-dark-tertiary px-2.5 py-2">
                                  <div className="flex items-center justify-between gap-2 mb-1">
                                    <h4 className="font-medium text-gray-900 dark:text-dark-primary text-xs truncate min-w-0">
                                      {region}
                                    </h4>
                                    <span className="text-xs font-semibold tabular-nums text-emerald-600 dark:text-emerald-400 flex-shrink-0">
                                      {regionPct}%
                                    </span>
                                  </div>
                                  <div className="h-1.5 rounded-full bg-gray-200 dark:bg-dark-primary overflow-hidden">
                                    <div
                                      className="h-full bg-emerald-500 transition-all duration-500"
                                      style={{ width: `${regionPct}%` }}
                                    />
                                  </div>
                                  <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
                                    {data.explored}/{data.total} explored
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>
        )}

        {/* Grid Count Display - Bottom Right */}
        <div className="fixed bottom-4 right-4 z-[1000] hidden lg:block">
          <div className="bg-white dark:bg-dark-secondary rounded-lg shadow-lg border border-gray-200 dark:border-dark-primary px-2 sm:px-3 py-1.5 sm:py-2 backdrop-blur-sm bg-opacity-95 dark:bg-opacity-95 transition-all duration-300 hover:shadow-xl hover:scale-105">
            <div className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
              Showing {filteredCells.length}/{gridData?.cells?.length || 0} grids
            </div>
          </div>
        </div>

        <MapContainer
          center={mapCenter}
          zoom={clampZoomForStyle(targetZoom, mapStyle)}
          minZoom={MAP_STYLES[mapStyle].minZoom}
          maxZoom={MAP_STYLES[mapStyle].maxZoom}
          style={{ position: 'absolute', inset: 0, height: '100%', width: '100%' }}
          zoomControl={false}
          attributionControl={false}
          ref={mapRef}
        >
          <MapZoomLimits
            minZoom={MAP_STYLES[mapStyle].minZoom}
            maxZoom={MAP_STYLES[mapStyle].maxZoom}
          />
          <MapUpdater
            center={mapCenter}
            zoom={clampZoomForStyle(targetZoom, mapStyle)}
            maxZoom={MAP_STYLES[mapStyle].maxZoom}
          />
          <MapViewWatcher onViewChange={handleMapViewChange} />
          <MapMotionWatcher onMovingChange={setIsMapMoving} />
          
          <TileLayer
            key={mapStyle}
            attribution={MAP_STYLES[mapStyle].attribution}
            url={MAP_STYLES[mapStyle].url}
            minZoom={MAP_STYLES[mapStyle].minZoom}
            maxZoom={MAP_STYLES[mapStyle].maxZoom}
            maxNativeZoom={MAP_STYLES[mapStyle].maxNativeZoom}
          />

          {/* Hide GPS while zooming/panning so the blue dot does not drift mid-gesture */}
          {userLocation && !isMapMoving && (
            <>
              <LeafletCircle
                center={userLocation.position}
                radius={userLocation.accuracy}
                pathOptions={{
                  color: '#3B82F6',
                  fillColor: '#3B82F6',
                  fillOpacity: 0.12,
                  weight: 1,
                  opacity: 0.45
                }}
              />
              <CircleMarker
                center={userLocation.position}
                radius={8}
                pathOptions={{
                  color: '#FFFFFF',
                  fillColor: '#2563EB',
                  fillOpacity: 1,
                  weight: 3
                }}
              />
            </>
          )}
          
          {visibleCells.map((cell: GridCell) => {
            const isExplored = userProgress?.exploredCells[cell.id]?.status === 'explored'
            const isInaccessible = userProgress?.exploredCells[cell.id]?.status === 'inaccessible'
            const userNotes = userProgress?.exploredCells[cell.id]?.notes
            const exploredDate = userProgress?.exploredCells[cell.id]?.exploredDate
            const customName = userProgress?.customNames?.[cell.id]
            const distanceLabel = userLocation
              ? getDistanceLabelForCell(userLocation.position, cell)
              : null
            
            return (
              <GridCellComponent
                key={cell.id}
                cell={cell}
                isExplored={isExplored}
                isInaccessible={isInaccessible}
                userNotes={userNotes}
                exploredDate={exploredDate}
                isDragging={isMapMoving}
                showOutlines={showOutlines}
                distanceLabel={distanceLabel}
                onCellClick={handleCellClick}
                onToggleExplored={handleToggleExplored}
                onMarkInaccessible={handleMarkInaccessible}
                onUpdateNotes={handleUpdateNotes}
                onUpdateExploredDate={handleUpdateExploredDate}
                onUpdateCustomName={handleUpdateCustomName}
                onRemoveCustomName={handleRemoveCustomName}
                customName={customName}
                isCelebrating={cell.id === celebratingCell}
                onContextMenu={handleContextMenu}
                onLongPressStart={handleLongPressStart}
                onLongPressEnd={handleLongPressEnd}
                isLongPressing={longPressCell === cell.id}
                isSelected={selectedCell?.id === cell.id}
              />
            )
          })}

          {/* Selection ring drawn last + non-interactive so all 4 sides stay even and long-press still works */}
          {selectedCell && (
            <Polygon
              key={`selection-${selectedCell.id}`}
              positions={selectedCell.bounds as [number, number][]}
              pathOptions={{
                color: '#F59E0B',
                weight: 3,
                opacity: 1,
                fillOpacity: 0,
                lineJoin: 'miter',
                lineCap: 'square',
                interactive: false
              }}
            />
          )}
        </MapContainer>
      </div>

      {/* Floating Grid Modal */}
      {showGridDetails && selectedCell && (() => {
        const cell = selectedCell
        const cellProgress = userProgress?.exploredCells?.[cell.id]
        const isExplored = cellProgress?.status === 'explored'
        const isInaccessible = cellProgress?.status === 'inaccessible'
        const userNotes = cellProgress?.notes || ''
        const exploredDate = cellProgress?.exploredDate || ''
        const customName = userProgress?.customNames?.[cell.id] || ''

        const handleSaveName = () => {
          if (editingNameValue.trim()) {
            if (editingNameValue.trim() === cell.displayName) {
              // If name is same as default, remove custom name
              handleRemoveCustomName(cell.id)
            } else {
              // Set custom name
              handleUpdateCustomName(cell.id, editingNameValue.trim())
            }
          }
          setEditingName(false)
        }

        const handleSaveDate = () => {
          if (editingDateValue) {
            handleUpdateExploredDate(cell.id, new Date(editingDateValue).toISOString())
          }
          setEditingDate(false)
        }

        const handleSaveNotes = () => {
          handleUpdateNotes(cell.id, editingNotesValue)
          setEditingNotes(false)
        }

        const handleStartEditingName = () => {
          setEditingNameValue(customName || cell.displayName)
          setEditingName(true)
        }

        const handleStartEditingDate = () => {
          setEditingDateValue(exploredDate ? new Date(exploredDate).toISOString().split('T')[0] : '')
          setEditingDate(true)
        }

        const handleStartEditingNotes = () => {
          setEditingNotesValue(userNotes || '')
          setEditingNotes(true)
        }

        return (
          <>
            {/* Mobile: Slide-out drawer from left */}
            <div className={`fixed inset-0 z-[1000] lg:hidden ${
              isModalCollapsed ? 'pointer-events-none' : ''
            }`}>
              {/* Backdrop */}
              <div 
                className={`absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300 ${
                  isModalCollapsed ? 'opacity-0 pointer-events-none' : ''
                }`}
                onClick={() => setIsModalCollapsed(true)}
              />
              
              {/* Floating collapse button when modal is collapsed */}
              {isModalCollapsed && (
                <button
                  onClick={() => setIsModalCollapsed(false)}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-[1001] p-3 bg-white dark:bg-dark-secondary rounded-full shadow-lg border border-gray-200 dark:border-dark-primary hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-all duration-200 pointer-events-auto"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-500 dark:text-gray-400 rotate-180" />
                </button>
              )}
              
              {/* Drawer */}
              <div className={`absolute left-0 top-0 h-full w-80 max-w-[85vw] bg-white dark:bg-dark-secondary shadow-2xl transform transition-transform duration-300 ease-out pointer-events-auto ${
                isModalCollapsed ? '-translate-x-full' : 'translate-x-0'
              }`}>
                <div className="h-full flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-primary">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-dark-primary text-lg truncate">
                        {customName || cell.displayName}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Grid #{cell.id} • {cell.regionName}
                      </p>
                      {userLocation && (
                        <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-singapore-blue">
                          <Navigation className="h-3 w-3" aria-hidden="true" />
                          {getDistanceLabelForCell(userLocation.position, cell)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setIsModalCollapsed(!isModalCollapsed)}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors duration-200"
                      >
                        <ChevronLeft className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                          isModalCollapsed ? 'rotate-180' : ''
                        }`} />
                      </button>
                      <button
                        onClick={closeModal}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors duration-200"
                      >
                        <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {/* Status Actions */}
                    <div className="flex flex-col space-y-2">
                      {!isExplored && !isInaccessible && (
                        <button
                          onClick={() => handleToggleExplored(cell.id, true)}
                          className="flex items-center justify-center space-x-2 px-3 py-2 bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/40 transition-colors duration-200"
                        >
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">Mark Explored</span>
                        </button>
                      )}
                      {!isInaccessible && (
                        <button
                          onClick={() => handleMarkInaccessible(cell.id)}
                          className="flex items-center justify-center space-x-2 px-3 py-2 bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors duration-200"
                        >
                          <Ban className="h-4 w-4" />
                          <span className="text-sm font-medium">Mark Inaccessible</span>
                        </button>
                      )}
                      {(isExplored || isInaccessible) && (
                        <button
                          onClick={() => handleToggleExplored(cell.id, false)}
                          className="flex items-center justify-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-900/40 transition-colors duration-200"
                        >
                          <Circle className="h-4 w-4" />
                          <span className="text-sm font-medium">Mark Unexplored</span>
                        </button>
                      )}
                    </div>

                    {/* Grid Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Grid Name
                      </label>
                      {editingName ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editingNameValue}
                            onChange={(e) => setEditingNameValue(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-300 dark:border-dark-primary rounded-xl focus:outline-none focus:ring-2 focus:ring-singapore-blue focus:border-transparent bg-white dark:bg-dark-tertiary text-gray-900 dark:text-dark-primary"
                            placeholder="Enter custom grid name..."
                          />
                          <div className="flex gap-2 w-full">
                            <button type="button" onClick={handleSaveName} className={DETAIL_BTN_SAVE}>
                              <Save className="h-4 w-4" aria-hidden="true" />
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingName(false)
                                setEditingNameValue(customName || cell.displayName)
                              }}
                              className={DETAIL_BTN_CANCEL}
                            >
                              <X className="h-4 w-4" aria-hidden="true" />
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-gray-900 dark:text-dark-primary truncate flex-1 min-w-0">
                            {customName || cell.displayName}
                          </span>
                          <button type="button" onClick={handleStartEditingName} className={DETAIL_BTN_EDIT}>
                            <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
                            Edit
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Date marked */}
                    {(isExplored || isInaccessible) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {isInaccessible ? 'Marked on' : 'Explored Date'}
                        </label>
                        {editingDate ? (
                          <div className="space-y-2">
                            <input
                              type="date"
                              value={editingDateValue}
                              onChange={(e) => setEditingDateValue(e.target.value)}
                              className="w-full px-3 py-2.5 border border-gray-300 dark:border-dark-primary rounded-xl focus:outline-none focus:ring-2 focus:ring-singapore-blue focus:border-transparent bg-white dark:bg-dark-tertiary text-gray-900 dark:text-dark-primary"
                            />
                            <div className="flex gap-2 w-full">
                              <button type="button" onClick={handleSaveDate} className={DETAIL_BTN_SAVE}>
                                <Save className="h-4 w-4" aria-hidden="true" />
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingDate(false)
                                  setEditingDateValue(exploredDate ? new Date(exploredDate).toISOString().split('T')[0] : '')
                                }}
                                className={DETAIL_BTN_CANCEL}
                              >
                                <X className="h-4 w-4" aria-hidden="true" />
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-gray-900 dark:text-dark-primary min-w-0">
                              {exploredDate ? formatDate(exploredDate) : 'Not set'}
                            </span>
                            <button type="button" onClick={handleStartEditingDate} className={DETAIL_BTN_EDIT}>
                              <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
                              Edit
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Notes
                      </label>
                      {editingNotes ? (
                        <div className="space-y-2">
                          <textarea
                            value={editingNotesValue}
                            onChange={(e) => setEditingNotesValue(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2.5 border border-gray-300 dark:border-dark-primary rounded-xl focus:outline-none focus:ring-2 focus:ring-singapore-blue focus:border-transparent bg-white dark:bg-dark-tertiary text-gray-900 dark:text-dark-primary resize-none"
                            placeholder="Add notes about this grid..."
                          />
                          <div className="flex gap-2 w-full">
                            <button type="button" onClick={handleSaveNotes} className={DETAIL_BTN_SAVE}>
                              <Save className="h-4 w-4" aria-hidden="true" />
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingNotes(false)
                                setEditingNotesValue(userNotes || '')
                              }}
                              className={DETAIL_BTN_CANCEL}
                            >
                              <X className="h-4 w-4" aria-hidden="true" />
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="min-h-[60px] p-3 bg-gray-50 dark:bg-dark-tertiary rounded-xl">
                            <p className="text-gray-900 dark:text-dark-primary text-sm whitespace-pre-wrap">
                              {userNotes || 'No notes added yet.'}
                            </p>
                          </div>
                          <button type="button" onClick={handleStartEditingNotes} className={DETAIL_BTN_EDIT}>
                            <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
                            {userNotes ? 'Edit Notes' : 'Add Notes'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop: Fixed modal (existing behavior) */}
            <div className="hidden lg:block fixed left-4 top-32 z-[1000] w-80 animate-slide-in">
              <div className="bg-white dark:bg-dark-secondary rounded-xl shadow-2xl border border-gray-200 dark:border-dark-primary p-6 backdrop-blur-sm bg-opacity-95 dark:bg-opacity-95 max-h-[70vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-dark-primary text-lg">
                      {customName || cell.displayName}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Grid #{cell.id} • {cell.regionName}
                    </p>
                    {userLocation && (
                      <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-singapore-blue">
                        <Navigation className="h-3 w-3" aria-hidden="true" />
                        {getDistanceLabelForCell(userLocation.position, cell)}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={closeModal}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors duration-200"
                  >
                    <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                {/* Status Actions */}
                <div className="flex items-center space-x-2 mb-4">
                  {!isExplored && !isInaccessible && (
                    <button
                      onClick={() => handleToggleExplored(cell.id, true)}
                      className="flex items-center space-x-2 px-3 py-2 bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/40 transition-colors duration-200"
                    >
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">Mark Explored</span>
                    </button>
                  )}
                  {!isInaccessible && (
                    <button
                      onClick={() => handleMarkInaccessible(cell.id)}
                      className="flex items-center space-x-2 px-3 py-2 bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors duration-200"
                    >
                      <Ban className="h-4 w-4" />
                      <span className="text-sm font-medium">Mark Inaccessible</span>
                    </button>
                  )}
                  {(isExplored || isInaccessible) && (
                    <button
                      onClick={() => handleToggleExplored(cell.id, false)}
                      className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-900/40 transition-colors duration-200"
                    >
                      <Circle className="h-4 w-4" />
                      <span className="text-sm font-medium">Mark Unexplored</span>
                    </button>
                  )}
                </div>

                {/* Grid Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Grid Name
                  </label>
                  {editingName ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editingNameValue}
                        onChange={(e) => setEditingNameValue(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-dark-primary rounded-xl focus:outline-none focus:ring-2 focus:ring-singapore-blue focus:border-transparent bg-white dark:bg-dark-tertiary text-gray-900 dark:text-dark-primary"
                        placeholder="Enter custom grid name..."
                      />
                      <div className="flex gap-2 w-full">
                        <button type="button" onClick={handleSaveName} className={DETAIL_BTN_SAVE}>
                          <Save className="h-4 w-4" aria-hidden="true" />
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingName(false)
                            setEditingNameValue(customName || cell.displayName)
                          }}
                          className={DETAIL_BTN_CANCEL}
                        >
                          <X className="h-4 w-4" aria-hidden="true" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-900 dark:text-dark-primary truncate min-w-0">
                        {customName || cell.displayName}
                      </span>
                      <button type="button" onClick={handleStartEditingName} className={DETAIL_BTN_EDIT}>
                        <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
                        Edit
                      </button>
                    </div>
                  )}
                </div>

                {/* Date marked */}
                {(isExplored || isInaccessible) && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {isInaccessible ? 'Marked on' : 'Explored Date'}
                    </label>
                    {editingDate ? (
                      <div className="space-y-2">
                        <input
                          type="date"
                          value={editingDateValue}
                          onChange={(e) => setEditingDateValue(e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-dark-primary rounded-xl focus:outline-none focus:ring-2 focus:ring-singapore-blue focus:border-transparent bg-white dark:bg-dark-tertiary text-gray-900 dark:text-dark-primary"
                        />
                        <div className="flex gap-2 w-full">
                          <button type="button" onClick={handleSaveDate} className={DETAIL_BTN_SAVE}>
                            <Save className="h-4 w-4" aria-hidden="true" />
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingDate(false)
                              setEditingDateValue(exploredDate ? new Date(exploredDate).toISOString().split('T')[0] : '')
                            }}
                            className={DETAIL_BTN_CANCEL}
                          >
                            <X className="h-4 w-4" aria-hidden="true" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-900 dark:text-dark-primary min-w-0">
                          {exploredDate ? formatDate(exploredDate) : 'Not set'}
                        </span>
                        <button type="button" onClick={handleStartEditingDate} className={DETAIL_BTN_EDIT}>
                          <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Notes
                  </label>
                  {editingNotes ? (
                    <div className="space-y-2">
                      <textarea
                        value={editingNotesValue}
                        onChange={(e) => setEditingNotesValue(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-dark-primary rounded-xl focus:outline-none focus:ring-2 focus:ring-singapore-blue focus:border-transparent bg-white dark:bg-dark-tertiary text-gray-900 dark:text-dark-primary resize-none"
                        placeholder="Add notes about this grid..."
                      />
                      <div className="flex gap-2 w-full">
                        <button type="button" onClick={handleSaveNotes} className={DETAIL_BTN_SAVE}>
                          <Save className="h-4 w-4" aria-hidden="true" />
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingNotes(false)
                            setEditingNotesValue(userNotes || '')
                          }}
                          className={DETAIL_BTN_CANCEL}
                        >
                          <X className="h-4 w-4" aria-hidden="true" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="min-h-[60px] p-3 bg-gray-50 dark:bg-dark-tertiary rounded-xl">
                        <p className="text-gray-900 dark:text-dark-primary text-sm whitespace-pre-wrap">
                          {userNotes || 'No notes added yet.'}
                        </p>
                      </div>
                      <button type="button" onClick={handleStartEditingNotes} className={DETAIL_BTN_EDIT}>
                        <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
                        {userNotes ? 'Edit Notes' : 'Add Notes'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )
      })()}

      {/* Help Modal */}
        <div
          className={`ui-modal-backdrop fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 ${
            showHelp ? 'is-open' : ''
          }`}
          aria-hidden={!showHelp}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowHelp(false)
          }}
        >
          <div className="ui-modal-panel bg-white dark:bg-dark-secondary rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-primary">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-singapore-blue/10 rounded-lg">
                  <HelpCircle className="h-6 w-6 text-singapore-blue" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-dark-primary">Keyboard Shortcuts</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Master the grid explorer with these shortcuts</p>
                </div>
              </div>
              <button
                onClick={() => setShowHelp(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors duration-200"
                tabIndex={showHelp ? 0 : -1}
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {/* Navigation Shortcuts */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-primary mb-4 flex items-center">
                    <Navigation className="h-5 w-5 mr-2 text-singapore-blue" />
                    Navigation
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-dark-tertiary rounded-lg">
                      <kbd className="px-2 py-1 bg-white dark:bg-dark-secondary border border-gray-300 dark:border-dark-primary rounded text-sm font-mono text-gray-700 dark:text-gray-300 min-w-[40px] text-center">S</kbd>
                      <span className="text-gray-700 dark:text-gray-300">Focus search box</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-dark-tertiary rounded-lg">
                      <kbd className="px-2 py-1 bg-white dark:bg-dark-secondary border border-gray-300 dark:border-dark-primary rounded text-sm font-mono text-gray-700 dark:text-gray-300 min-w-[40px] text-center">M</kbd>
                      <span className="text-gray-700 dark:text-gray-300">Toggle menu</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-dark-tertiary rounded-lg">
                      <kbd className="px-2 py-1 bg-white dark:bg-dark-secondary border border-gray-300 dark:border-dark-primary rounded text-sm font-mono text-gray-700 dark:text-gray-300 min-w-[40px] text-center">C</kbd>
                      <span className="text-gray-700 dark:text-gray-300">Center on Singapore</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-dark-tertiary rounded-lg">
                      <kbd className="px-2 py-1 bg-white dark:bg-dark-secondary border border-gray-300 dark:border-dark-primary rounded text-sm font-mono text-gray-700 dark:text-gray-300 min-w-[40px] text-center">ESC</kbd>
                      <span className="text-gray-700 dark:text-gray-300">Close modals</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-dark-tertiary rounded-lg">
                      <kbd className="px-2 py-1 bg-white dark:bg-dark-secondary border border-gray-300 dark:border-dark-primary rounded text-sm font-mono text-gray-700 dark:text-gray-300 min-w-[40px] text-center">H</kbd>
                      <span className="text-gray-700 dark:text-gray-300">Open help</span>
                    </div>
                  </div>
                </div>

                {/* Search Shortcuts */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-primary mb-4 flex items-center">
                    <Search className="h-5 w-5 mr-2 text-singapore-blue" />
                    Search & Filtering
                  </h3>
                  <div className="space-y-3">
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">Search Operators</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <kbd className="px-2 py-1 bg-white dark:bg-dark-secondary border border-blue-300 dark:border-blue-700 rounded text-xs font-mono text-blue-700 dark:text-blue-300">=explored</kbd>
                          <span className="text-blue-800 dark:text-blue-200">Show all explored grids</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <kbd className="px-2 py-1 bg-white dark:bg-dark-secondary border border-blue-300 dark:border-blue-700 rounded text-xs font-mono text-blue-700 dark:text-blue-300">=unexplored</kbd>
                          <span className="text-blue-800 dark:text-blue-200">Show all unexplored grids</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <kbd className="px-2 py-1 bg-white dark:bg-dark-secondary border border-blue-300 dark:border-blue-700 rounded text-xs font-mono text-blue-700 dark:text-blue-300">=inaccessible</kbd>
                          <span className="text-blue-800 dark:text-blue-200">Show all inaccessible grids</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <kbd className="px-2 py-1 bg-white dark:bg-dark-secondary border border-blue-300 dark:border-blue-700 rounded text-xs font-mono text-blue-700 dark:text-blue-300">=random</kbd>
                          <span className="text-blue-800 dark:text-blue-200">Go to random unexplored grid</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <kbd className="px-2 py-1 bg-white dark:bg-dark-secondary border border-blue-300 dark:border-blue-700 rounded text-xs font-mono text-blue-700 dark:text-blue-300">=all</kbd>
                          <span className="text-blue-800 dark:text-blue-200">Show all grids</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <kbd className="px-2 py-1 bg-white dark:bg-dark-secondary border border-blue-300 dark:border-blue-700 rounded text-xs font-mono text-blue-700 dark:text-blue-300">=share</kbd>
                          <span className="text-blue-800 dark:text-blue-200">Share your progress</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Interaction Shortcuts */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-primary mb-4 flex items-center">
                    <MousePointer className="h-5 w-5 mr-2 text-singapore-blue" />
                    Grid Interaction
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-dark-tertiary rounded-lg">
                      <MousePointer className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      <span className="text-gray-700 dark:text-gray-300">Desktop click: Open grid details · Phone tap: Select only</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-dark-tertiary rounded-lg">
                      <MousePointer className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      <span className="text-gray-700 dark:text-gray-300">Right-click: Open status menu</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-dark-tertiary rounded-lg">
                      <MousePointer className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      <span className="text-gray-700 dark:text-gray-300">Long press / right-click: Menu (details + status)</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-dark-tertiary rounded-lg">
                      <MousePointer className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      <span className="text-gray-700 dark:text-gray-300">Hover: Show grid info</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-dark-tertiary rounded-lg md:col-span-2">
                      <LocateFixed className="h-4 w-4 text-singapore-blue" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Top-left GPS button: show your location (blue dot) and mark that square explored if accuracy is under 75m
                      </span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-dark-tertiary rounded-lg md:col-span-2">
                      <Layers className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Layers button: switch map style (Streets, Clean, Dark)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Stats Shortcuts */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-primary mb-4 flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2 text-singapore-blue" />
                    Statistics
                  </h3>
                  <div className="p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg">
                    <p className="text-sm text-green-800 dark:text-green-200 mb-3">
                      Click on any stat number to filter and zoom to those grids:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-green-700 dark:text-green-300">Explored grids</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                        <span className="text-sm text-green-700 dark:text-green-300">Unexplored grids</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <span className="text-sm text-green-700 dark:text-green-300">Inaccessible grids</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tips */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-primary mb-4 flex items-center">
                    <Lightbulb className="h-5 w-5 mr-2 text-singapore-blue" />
                    Pro Tips
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3 p-3 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <Lightbulb className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-yellow-800 dark:text-yellow-200">
                        Use <kbd className="px-1 py-0.5 bg-white dark:bg-dark-secondary border border-yellow-300 dark:border-yellow-700 rounded text-xs font-mono">=random</kbd> to discover new areas quickly
                      </span>
                    </div>
                    <div className="flex items-start space-x-3 p-3 bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-lg">
                      <Lightbulb className="h-4 w-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-purple-800 dark:text-purple-200">
                        Right-click and long-press work anywhere on the map for quick marking
                      </span>
                    </div>
                    <div className="flex items-start space-x-3 p-3 bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                      <Lightbulb className="h-4 w-4 text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-indigo-800 dark:text-indigo-200">
                        Custom grid names are searchable and help you remember special locations
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Settings Modal */}
        <div
          className={`ui-modal-backdrop fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 ${
            showSettings ? 'is-open' : ''
          }`}
          aria-hidden={!showSettings}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowSettings(false)
          }}
        >
          <div className="ui-modal-panel bg-white dark:bg-dark-secondary rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-primary">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-singapore-blue/10 rounded-lg">
                  <Settings className="h-6 w-6 text-singapore-blue" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-dark-primary">Settings</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Customize your exploration experience</p>
                </div>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors duration-200"
                tabIndex={showSettings ? 0 : -1}
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {/* Data Management */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-primary mb-4 flex items-center">
                    <Database className="h-5 w-5 mr-2 text-singapore-blue" />
                    Data Management
                  </h3>
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">Export Your Data</h4>
                      <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                        Download a backup of all your exploration data including explored grids, custom names, notes, and dates.
                      </p>
                      <button
                        onClick={exportUserData}
                        disabled={!user || !userProgress}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        <Download className="h-4 w-4" />
                        <span>Export Data</span>
                      </button>
                    </div>

                    <div className="p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg">
                      <h4 className="font-medium text-green-900 dark:text-green-300 mb-2">Import Data</h4>
                      <p className="text-sm text-green-800 dark:text-green-200 mb-3">
                        Import previously exported data. This will merge with your current data.
                      </p>
                      <label className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer transition-colors duration-200">
                        <Upload className="h-4 w-4" />
                        <span>Import Data</span>
                        <input
                          type="file"
                          accept=".json"
                          onChange={importUserData}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Display Settings */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-primary mb-4 flex items-center">
                    <Monitor className="h-5 w-5 mr-2 text-singapore-blue" />
                    Display Settings
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-tertiary rounded-lg">
                      <div>
                        <span className="text-gray-900 dark:text-dark-primary font-medium">Theme</span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Choose light or dark mode</p>
                      </div>
                      <button
                        onClick={toggleTheme}
                        className="p-2 rounded-lg bg-gray-100 dark:bg-dark-primary hover:bg-gray-200 dark:hover:bg-dark-tertiary transition-colors duration-200"
                      >
                        {theme === 'light' ? (
                          <Moon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                        ) : (
                          <Sun className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Exploration Settings */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-primary mb-4 flex items-center">
                    <MapPin className="h-5 w-5 mr-2 text-singapore-blue" />
                    Exploration Settings
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-tertiary rounded-lg">
                      <div>
                        <span className="text-gray-900 dark:text-dark-primary font-medium">Celebration Animations</span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Confetti and +1 when marking grids as explored</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={celebrationsEnabled}
                        onClick={toggleCelebrations}
                        className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                          celebrationsEnabled ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                            celebrationsEnabled ? 'translate-x-6' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Statistics */}
                {stats && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-primary mb-4 flex items-center">
                      <BarChart3 className="h-5 w-5 mr-2 text-singapore-blue" />
                      Your Statistics
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="p-3 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.exploredCells}</div>
                        <div className="text-sm text-green-700 dark:text-green-300">Explored</div>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-900/10 border border-gray-200 dark:border-gray-800 rounded-lg text-center">
                        <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">{stats.unexploredCells}</div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">Unexplored</div>
                      </div>
                      <div className="p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg text-center">
                        <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.inaccessibleCells}</div>
                        <div className="text-sm text-red-700 dark:text-red-300">Inaccessible</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      {/* Unified toast — solid, high-contrast, safe-area aware */}
      {toast && (
        <div
          role={toast.type === 'error' ? 'alert' : 'status'}
          aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
          className="fixed z-[3000] left-1/2 -translate-x-1/2 w-[min(22rem,calc(100vw-1.5rem))] pointer-events-none"
          style={{ top: 'max(4.5rem, calc(env(safe-area-inset-top, 0px) + 3.75rem))' }}
        >
          <div
            className={`ui-toast flex items-start gap-3 rounded-2xl border bg-white p-3.5 shadow-2xl ring-1 ring-black/10 dark:bg-neutral-900 dark:ring-white/10 sm:p-4 ${
              toastOpen ? 'is-open' : ''
            } ${
              toast.type === 'success'
                ? 'border-l-4 border-l-emerald-500 border-y-gray-200 border-r-gray-200 dark:border-y-neutral-700 dark:border-r-neutral-700'
                : toast.type === 'warning'
                ? 'border-l-4 border-l-amber-500 border-y-gray-200 border-r-gray-200 dark:border-y-neutral-700 dark:border-r-neutral-700'
                : toast.type === 'info'
                ? 'border-l-4 border-l-sky-500 border-y-gray-200 border-r-gray-200 dark:border-y-neutral-700 dark:border-r-neutral-700'
                : 'border-l-4 border-l-red-500 border-y-gray-200 border-r-gray-200 dark:border-y-neutral-700 dark:border-r-neutral-700'
            }`}
          >
            <div
              className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
                toast.type === 'success'
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
                  : toast.type === 'warning'
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300'
                  : toast.type === 'info'
                  ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300'
                  : 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300'
              }`}
            >
              {toast.type === 'success' ? (
                <CheckCircle className="h-5 w-5" aria-hidden="true" />
              ) : toast.type === 'warning' ? (
                <AlertTriangle className="h-5 w-5" aria-hidden="true" />
              ) : toast.type === 'info' ? (
                <Info className="h-5 w-5" aria-hidden="true" />
              ) : (
                <AlertCircle className="h-5 w-5" aria-hidden="true" />
              )}
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {toast.title}
              </h3>
              <p className="mt-0.5 text-sm leading-snug text-gray-600 dark:text-gray-300">
                {toast.message}
              </p>
            </div>
            <button
              type="button"
              onClick={dismissToast}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-neutral-800 dark:hover:text-white"
              aria-label="Dismiss notification"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default GridExplorer